// English as Second Language (ESL) Curriculum Data - Vanuatu National Syllabus
// Years 8-13 Secondary
// Last Updated: February 2026

export const eslCurriculum = {
  year8: {
    title: "Year 8 English as Second Language",
    description: "Building on Year 7 foundations with communicative, collaborative, and task-based approaches to develop listening, speaking, reading, writing, and viewing skills in English",
    hours: 102,
    strands: [
      {
        id: "8ESL1",
        name: "Listening",
        icon: "MessageSquare",
        color: "blue",
        hours: 20,
        weight: 20,
        outcome: "Listen to, comprehend and explain aural texts in English from familiar and some unfamiliar contexts",
        subStrands: [
          {
            id: "8ESL1.1",
            name: "Skills and Strategies",
            outcome: "Listen to aural texts drawn from familiar and some unfamiliar contexts, drawing on contextual clues and background knowledge to explain main ideas and attitudes",
            topics: [
              { code: "8ESL1.1.1", topic: "Interpret sounds, stress, intonation and volume", level: 1 },
              { code: "8ESL1.1.2", topic: "Recognise and explain how sounds, stress, intonation and volume can be used for different effects and meaning", level: 1 },
              { code: "8ESL1.1.3", topic: "Identify and explain the meaning of key words and phrases in texts drawn from familiar and some unfamiliar contexts", level: 1 },
              { code: "8ESL1.1.4", topic: "Explain the meaning of idioms and colloquialisms in texts drawn from familiar and some unfamiliar contexts", level: 2 },
              { code: "8ESL1.1.5", topic: "Comprehend word order patterns in familiar and some unfamiliar contexts", level: 2 },
              { code: "8ESL1.1.6", topic: "Identify the main ideas of authentic subject-specific (e.g. scientific or historical) aural texts", level: 2 },
              { code: "8ESL1.1.7", topic: "Listen for, and explain, main points in texts on familiar and some unfamiliar topics, using contextual clues and background knowledge", level: 3 },
              { code: "8ESL1.1.8", topic: "Apply note-taking skills, including the use of graphic organisers and paraphrasing, to identify and present the main ideas in texts", level: 3 },
              { code: "8ESL1.1.9", topic: "Use strategies to engage with unfamiliar topics (e.g. asking for explanation, requesting extra time to process and comprehend)", level: 3 }
            ]
          },
          {
            id: "8ESL1.2",
            name: "Production",
            outcome: "Summarise the content of aural texts and interactions, making some inferences, using appropriate questions to clarify; and reflect on audio text content",
            topics: [
              { code: "8ESL1.2.1", topic: "Summarise the main ideas and key attitudes expressed in a variety of aural texts", level: 1 },
              { code: "8ESL1.2.2", topic: "Draw general conclusions and provide personal comment on the main ideas in aural texts", level: 2 },
              { code: "8ESL1.2.3", topic: "Use a variety of question types (closed, open, probing, leading) to clarify information and reflect on main ideas", level: 2 },
              { code: "8ESL1.2.4", topic: "Respond appropriately to a range of questions on familiar and some unfamiliar topics drawn from texts studied", level: 3 },
              { code: "8ESL1.2.5", topic: "Interpret and respond to common cultural references and humour in familiar contexts", level: 3 }
            ]
          },
          {
            id: "8ESL1.3",
            name: "Texts and Contexts",
            outcome: "Identify and respond appropriately to verbal and non-verbal cues; and describe the audience and purpose of aural texts",
            topics: [
              { code: "8ESL1.3.1", topic: "Identify the meaning of particular verbal cues (sounds, stress, intonation, volume) and non-verbal cues (eye contact, body language, facial expressions)", level: 1 },
              { code: "8ESL1.3.2", topic: "Respond appropriately to verbal and non-verbal cues", level: 2 },
              { code: "8ESL1.3.3", topic: "Use active listening skills appropriate to the context (listening to a speaker, participating in a debate, having a conversation)", level: 2 },
              { code: "8ESL1.3.4", topic: "Describe the intended purpose and audience of aural texts from familiar and some unfamiliar contexts", level: 3 },
              { code: "8ESL1.3.5", topic: "Use English and bilingual dictionaries for clarification of meaning when necessary", level: 1 }
            ]
          }
        ]
      },
      {
        id: "8ESL2",
        name: "Speaking",
        icon: "MessageSquare",
        color: "green",
        hours: 20,
        weight: 20,
        outcome: "Use spoken English to engage with clarity and purpose in a variety of familiar contexts and in some unfamiliar contexts",
        subStrands: [
          {
            id: "8ESL2.1",
            name: "Skills and Strategies",
            outcome: "Engage in planned and spontaneous verbal interactions accurately using common idiomatic language and a range of grammatical structures",
            topics: [
              { code: "8ESL2.1.1", topic: "Engage effectively in verbal interactions on familiar and some unfamiliar topics", level: 1 },
              { code: "8ESL2.1.2", topic: "Initiate and maintain interactions with autonomy in school contexts", level: 1 },
              { code: "8ESL2.1.3", topic: "Offer a point of view on familiar and some unfamiliar topics and attempt to justify opinion", level: 2 },
              { code: "8ESL2.1.4", topic: "Code-switch according to different language and cultural contexts (e.g. formal English with principal, informal Bislama at home)", level: 2 },
              { code: "8ESL2.1.5", topic: "Use simple, compound and complex sentences", level: 2 },
              { code: "8ESL2.1.6", topic: "Use appropriate pronunciation, stress and intonation", level: 1 },
              { code: "8ESL2.1.7", topic: "Use common idioms and colloquial terms and phrases", level: 3 },
              { code: "8ESL2.1.8", topic: "Ask and answer unprepared questions on familiar and some unfamiliar topics", level: 3 }
            ]
          },
          {
            id: "8ESL2.2",
            name: "Production",
            outcome: "Select and use appropriate vocabulary to express a position in a variety of familiar and some unfamiliar contexts; and create oral texts for enjoyment and academic purposes",
            topics: [
              { code: "8ESL2.2.1", topic: "Use a range of common and some subject specific vocabulary to explain main ideas and express a point of view", level: 1 },
              { code: "8ESL2.2.2", topic: "Create and present structured and cohesive oral texts for enjoyment and academic purposes (re-tell a story, explain a procedure, deliver a presentation)", level: 2 },
              { code: "8ESL2.2.3", topic: "Collaborate with peers to create oral texts (interviews, panel discussions, group presentations)", level: 2 },
              { code: "8ESL2.2.4", topic: "Use some descriptive and emotive language (adverbs, metaphor, simile, alliteration) to create imagery in oral texts", level: 3 },
              { code: "8ESL2.2.5", topic: "Use culturally appropriate non-verbal communication strategies (eye contact, gestures, facial expressions) to support verbal communication", level: 2 },
              { code: "8ESL2.2.6", topic: "Apply self-repair strategies (rephrase, repeat or qualify words or phrases) as required when speaking", level: 3 }
            ]
          },
          {
            id: "8ESL2.3",
            name: "Texts and Contexts",
            outcome: "Explain the effective use of different spoken language across a range of familiar and some unfamiliar contexts",
            topics: [
              { code: "8ESL2.3.1", topic: "Identify and explain how particular vocabulary in texts is used given the audience and context", level: 2 },
              { code: "8ESL2.3.2", topic: "Identify some simple descriptive and emotive language (metaphor and simile) in texts and explain how it creates a certain effect", level: 3 },
              { code: "8ESL2.3.3", topic: "Recognise and respond appropriately to common cultural references and humour in personal and school contexts", level: 2 },
              { code: "8ESL2.3.4", topic: "Identify and explain the reasons for code switching in community and school contexts", level: 3 }
            ]
          }
        ]
      },
      {
        id: "8ESL3",
        name: "Reading",
        icon: "BookOpen",
        color: "purple",
        hours: 22,
        weight: 22,
        outcome: "Read and respond to a variety of texts on familiar and on simple unfamiliar topics",
        subStrands: [
          {
            id: "8ESL3.1",
            name: "Skills and Strategies",
            outcome: "Select and apply a range of skills and strategies to interpret and respond to a variety of texts on familiar and some unfamiliar topics; and use a metalanguage",
            topics: [
              { code: "8ESL3.1.1", topic: "Decode new vocabulary using known spelling patterns, prefixes and suffixes", level: 1 },
              { code: "8ESL3.1.2", topic: "Use strategies including previewing, predicting, decoding, questioning and summarising to interpret a variety of texts", level: 1 },
              { code: "8ESL3.1.3", topic: "Identify the organisational features (heading, sub-heading, bullet points, numbered lists, paragraphs, labels, graphics) of a variety of texts", level: 1 },
              { code: "8ESL3.1.4", topic: "Skim and scan texts to identify main ideas and determine relevance and usefulness when researching topics", level: 2 },
              { code: "8ESL3.1.5", topic: "Recognise common metalanguage (noun, verb, adjective)", level: 1 },
              { code: "8ESL3.1.6", topic: "Use metalanguage to discuss the elements (grammar, language and structure) of texts when reading or editing them", level: 3 },
              { code: "8ESL3.1.7", topic: "Find or select relevant printed and electronic fiction and non-fiction texts from a range of sources to read for pleasure and school work", level: 2 },
              { code: "8ESL3.1.8", topic: "Use a range of dictionaries and thesauruses (print and electronic, English and bilingual) for different purposes", level: 2 }
            ]
          },
          {
            id: "8ESL3.2",
            name: "Production",
            outcome: "Summarise and explain main points in texts on familiar and some unfamiliar topics; respond to arguments; infer some meaning; and identify and interpret common figurative language",
            topics: [
              { code: "8ESL3.2.1", topic: "Identify and summarise the main idea/s of a variety of texts on familiar and some unfamiliar topics", level: 1 },
              { code: "8ESL3.2.2", topic: "Use a range of graphic organisers (fishbone diagram, flow chart, mind map, Venn diagram) to explore and respond to the main ideas in texts", level: 2 },
              { code: "8ESL3.2.3", topic: "Use prior knowledge and textual information to draw conclusions, make critical judgements, and infer meaning in texts", level: 3 },
              { code: "8ESL3.2.4", topic: "Respond to viewpoints and arguments presented in texts by giving an opinion supported with evidence", level: 3 },
              { code: "8ESL3.2.5", topic: "Identify and interpret the meaning of common figurative language (similes, metaphors, idioms, colloquial language)", level: 2 }
            ]
          },
          {
            id: "8ESL3.3",
            name: "Texts and Contexts",
            outcome: "Explain the purpose, audience, language and structure of a range of informative and narrative texts; and identify common stereotypes and cultural perspectives",
            topics: [
              { code: "8ESL3.3.1", topic: "Identify and explain how texts vary depending on their purpose, intended audience and context", level: 2 },
              { code: "8ESL3.3.2", topic: "Identify and explain how the language of texts varies according to their purpose and audience", level: 2 },
              { code: "8ESL3.3.3", topic: "Identify and explain how the structure of informative and narrative texts varies according to their purpose and audience", level: 3 },
              { code: "8ESL3.3.4", topic: "Identify common stereotypes presented in texts and explain their effect on readers", level: 3 },
              { code: "8ESL3.3.5", topic: "Recognise when and how cultural perspectives are presented in texts", level: 3 },
              { code: "8ESL3.3.6", topic: "Reflect on how different cultural perspectives of readers can affect the interpretation of texts", level: 3 }
            ]
          }
        ]
      },
      {
        id: "8ESL4",
        name: "Writing",
        icon: "PenTool",
        color: "orange",
        hours: 22,
        weight: 22,
        outcome: "Write structured texts of different types with a sense of audience and purpose with some supporting evidence and use of complex language structures",
        subStrands: [
          {
            id: "8ESL4.1",
            name: "Skills and Strategies",
            outcome: "Use grammatically correct language, writing a range of sentence structures, employing spelling strategies and recognising common figurative language when creating texts",
            topics: [
              { code: "8ESL4.1.1", topic: "Write a range of simple, compound and complex sentences", level: 1 },
              { code: "8ESL4.1.2", topic: "Use a variety of cohesive devices (coordinating and subordinating conjunctions, adverbs, referential pronouns) to link ideas between words, phrases and paragraphs", level: 2 },
              { code: "8ESL4.1.3", topic: "Spell familiar words accurately and use spelling strategies (sounding out, chunking, rhyming, word families and rules) for more complex words", level: 1 },
              { code: "8ESL4.1.4", topic: "Accurately use punctuation including capital letters, full stops, question marks, commas, apostrophes, and quotation marks for dialogue", level: 1 },
              { code: "8ESL4.1.5", topic: "Develop familiarity with simple and continuous present; past and future tense; past participles; present and past passive; present and past perfect simple tense", level: 2 },
              { code: "8ESL4.1.6", topic: "Develop familiarity with nominalisation, adverbial phrases, adjective clauses and appositives", level: 3 },
              { code: "8ESL4.1.7", topic: "Recognise and use common figurative language (similes, metaphors, idioms, colloquial language)", level: 3 },
              { code: "8ESL4.1.8", topic: "Use dictionaries and thesauruses to check meaning, spell correctly and find synonyms", level: 1 }
            ]
          },
          {
            id: "8ESL4.2",
            name: "Production",
            outcome: "Create texts across a small range of genres on familiar and some unfamiliar topics, using evidence to support ideas and a range of vocabulary",
            topics: [
              { code: "8ESL4.2.1", topic: "Brainstorm, research and plan ideas using a range of graphic organisers", level: 1 },
              { code: "8ESL4.2.2", topic: "Use a range of common and subject-specific vocabulary", level: 1 },
              { code: "8ESL4.2.3", topic: "Organise ideas into different types of paragraphs (opinion, compare and contrast, cause and effect)", level: 2 },
              { code: "8ESL4.2.4", topic: "Use evidence, such as personal experience and examples, or text references in essays, to support viewpoints and opinions", level: 2 },
              { code: "8ESL4.2.5", topic: "Create cohesive texts using features appropriate to purpose and audience, for genres including informal and formal letters, reviews, blogs, essays and speeches", level: 3 },
              { code: "8ESL4.2.6", topic: "Edit and proofread drafts using strategies and resources (teacher conferencing, peer review and group workshops)", level: 2 },
              { code: "8ESL4.2.7", topic: "Publish and present or display final copies of own writing", level: 2 }
            ]
          },
          {
            id: "8ESL4.3",
            name: "Texts and Contexts",
            outcome: "Recognise and describe the features of a small range of genres and use these effectively for purpose and audience when creating texts",
            topics: [
              { code: "8ESL4.3.1", topic: "Recognise and describe the generic features (paragraphs, headings and subheadings, graphics) of a small range of genres, such as feature articles, persuasive speeches and essays", level: 2 },
              { code: "8ESL4.3.2", topic: "Identify the language (persuasive, emotive, formal or informal) used in a small range of genres", level: 2 },
              { code: "8ESL4.3.3", topic: "Create texts which use the appropriate generic features and language for purpose and audience", level: 3 },
              { code: "8ESL4.3.4", topic: "Recognise how some generic features of common texts may be different between English and the home language", level: 3 }
            ]
          }
        ]
      },
      {
        id: "8ESL5",
        name: "Viewing",
        icon: "FileText",
        color: "pink",
        hours: 18,
        weight: 16,
        outcome: "View, explain and create a variety of visual and multimodal texts on familiar and some unfamiliar topics",
        subStrands: [
          {
            id: "8ESL5.1",
            name: "Skills and Strategies",
            outcome: "Select skills and strategies to interpret a range of multimodal or visual texts on familiar topics, describing their purpose and structure, and explaining their effect",
            topics: [
              { code: "8ESL5.1.1", topic: "Use strategies including previewing, predicting, questioning and summarising to interpret visual and multimodal texts", level: 1 },
              { code: "8ESL5.1.2", topic: "Summarise the purpose and main idea of a range of texts (photojournalism, picture book, kastom dance, short film, comic strip, animation, advertisement)", level: 2 },
              { code: "8ESL5.1.3", topic: "Describe key features of visual and multimodal texts (colour, shot, angle, font, symbols, written text, composition, camera movement, dialogue, sound effects, music)", level: 2 },
              { code: "8ESL5.1.4", topic: "Explain how key features in visual and multimodal texts are used for particular effects", level: 3 },
              { code: "8ESL5.1.5", topic: "Select relevant visual or multimedia texts from a range of sources to view for pleasure and school work", level: 1 }
            ]
          },
          {
            id: "8ESL5.2",
            name: "Production",
            outcome: "Create visual and multimodal texts for a range of purposes and audiences, using appropriate structure and features",
            topics: [
              { code: "8ESL5.2.1", topic: "Create visual and multimodal texts on familiar topics, selecting features appropriate to audience and purpose (picture book, kastom dance, advertisement, poster, photo essay)", level: 2 },
              { code: "8ESL5.2.2", topic: "Describe the visual, structural and language features used in their own creations", level: 2 },
              { code: "8ESL5.2.3", topic: "Justify their choice of features in visual and multimodal creations", level: 3 },
              { code: "8ESL5.2.4", topic: "Explain their choice of visual language and structure to achieve a particular purpose and evaluate if this has been achieved", level: 3 }
            ]
          },
          {
            id: "8ESL5.3",
            name: "Texts and Contexts",
            outcome: "Explain how multimodal or visual texts from familiar and non-familiar situations are created for particular purposes and audiences",
            topics: [
              { code: "8ESL5.3.1", topic: "Identify and compare a range of visual and multimodal text types in social and academic life", level: 1 },
              { code: "8ESL5.3.2", topic: "Explain how visual and multimodal texts are created for different purposes and audiences", level: 2 },
              { code: "8ESL5.3.3", topic: "Explain how and why texts can be interpreted differently depending on context", level: 3 },
              { code: "8ESL5.3.4", topic: "Explain how and why texts can impact audiences in different ways", level: 3 }
            ]
          }
        ]
      }
    ],
    assessmentStructure: {
      internal: { weight: 60, components: ["Formative Assessments", "Class Tests", "Oral Presentations", "Assignments", "Writing Tasks"] },
      external: { weight: 40, components: ["End of Year Examination"] }
    }
  },
  year9: {
    title: "Year 9 English as Second Language",
    description: "Intermediate ESL skills focusing on listening, speaking, reading, writing and viewing in social, vocational, and educational contexts based on the Vanuatu National Syllabus",
    hours: 165,
    strands: [
      {
        id: "9ESL1",
        name: "Listening",
        icon: "Headphones",
        color: "purple",
        hours: 30,
        weight: 10,
        outcome: "Demonstrate understanding of extended aural texts from a range of social, vocational, and educational contexts",
        subStrands: [
          {
            id: "9ESL1.1",
            name: "Skills and Strategies",
            outcome: "Listen to extended aural texts, identify idioms and colloquialisms, and distinguish relevant from irrelevant information",
            topics: [
              { code: "9ESL1.1.2.1", topic: "Demonstrate understanding of how to listen to extended aural texts from a range of contexts (TEDx talks, presentations, interviews)", level: 2 },
              { code: "9ESL1.1.2.2", topic: "Identify use of idiom and colloquialism in an aural text", level: 2 },
              { code: "9ESL1.1.2.3", topic: "Identify differences between relevant and irrelevant information", level: 2 },
              { code: "9ESL1.1.2.4", topic: "State different emotions or vocal delivery in an aural text (pitch, stress, volume, pacing, pause, repetition)", level: 2 },
              { code: "9ESL1.1.2.5", topic: "Describe the effect of features in an aural text using appropriate metalanguages", level: 2 },
              { code: "9ESL1.1.3.1", topic: "Explain the meaning of idioms and colloquialisms in familiar and unfamiliar contexts", level: 3 },
              { code: "9ESL1.1.3.2", topic: "Distinguish between fact and opinion using knowledge of evaluative languages", level: 3 }
            ]
          },
          {
            id: "9ESL1.2",
            name: "Production",
            outcome: "Use active listening skills and produce responses to aural texts",
            topics: [
              { code: "9ESL1.2.2.1", topic: "Identify strengths and areas for development through reflecting on listening skills", level: 2 },
              { code: "9ESL1.2.2.2", topic: "Use question types appropriate to the context and communication need", level: 2 },
              { code: "9ESL1.2.2.3", topic: "Answer inferential questions about an aural text", level: 2 },
              { code: "9ESL1.2.3.1", topic: "Paraphrase what is heard in an aural text", level: 3 },
              { code: "9ESL1.2.3.2", topic: "Summarise extended aural texts using knowledge of text structure and language", level: 3 },
              { code: "9ESL1.2.3.3", topic: "Present a personal response to an aural text, citing evidence from the text", level: 3 }
            ]
          },
          {
            id: "9ESL1.3",
            name: "Texts and Contexts",
            outcome: "Understand verbal and non-verbal cues and cultural references in aural texts",
            topics: [
              { code: "9ESL1.3.2.1", topic: "Identify features of verbal and non-verbal cues conveyed in interaction", level: 2 },
              { code: "9ESL1.3.2.2", topic: "Identify the intended purpose and specific audiences for aural texts", level: 2 },
              { code: "9ESL1.3.3.1", topic: "Explain how language and structure used in aural texts is suited for specific audience and purpose", level: 3 },
              { code: "9ESL1.3.3.2", topic: "Explain how values and beliefs held by speakers are conveyed through aural texts", level: 3 },
              { code: "9ESL1.3.3.3", topic: "Interpret and respond to cultural references and humour in aural texts", level: 3 }
            ]
          }
        ]
      },
      {
        id: "9ESL2",
        name: "Speaking",
        icon: "MessageSquare",
        color: "orange",
        hours: 30,
        weight: 10,
        outcome: "Use spoken English with clarity and control for a range of social, vocational, and educational contexts",
        subStrands: [
          {
            id: "9ESL2.1",
            name: "Skills and Strategies",
            outcome: "Engage in classroom interactions, public speaking, debates, and discussions using appropriate register",
            topics: [
              { code: "9ESL2.1.2.1", topic: "Engage in classroom learning interactions independently", level: 2 },
              { code: "9ESL2.1.2.2", topic: "Use language features of public speaking", level: 2 },
              { code: "9ESL2.1.2.3", topic: "Use appropriate registers for different speaking contexts", level: 2 },
              { code: "9ESL2.1.2.4", topic: "Use simple, compound, and complex sentences accurately", level: 2 },
              { code: "9ESL2.1.3.1", topic: "Participate in debates and discussions", level: 3 },
              { code: "9ESL2.1.3.2", topic: "Express opinions clearly with elaborations and justification", level: 3 },
              { code: "9ESL2.1.3.3", topic: "Express opinions using idioms and colloquialisms relevant to purpose", level: 3 }
            ]
          },
          {
            id: "9ESL2.2",
            name: "Production",
            outcome: "Use technical terminology, cohesive devices, and figurative language in oral presentations",
            topics: [
              { code: "9ESL2.2.2.1", topic: "Use range of technical terminology with accuracy and fluency", level: 2 },
              { code: "9ESL2.2.2.2", topic: "Use range of cohesive devices to support the listener's attention", level: 2 },
              { code: "9ESL2.2.3.1", topic: "Use figurative language to affect the listener (metaphor, simile, hyperbole)", level: 3 },
              { code: "9ESL2.2.3.2", topic: "Present longer well-structured oral texts in collaborative settings", level: 3 },
              { code: "9ESL2.2.3.3", topic: "Use appropriate non-verbal communication strategies", level: 3 }
            ]
          },
          {
            id: "9ESL2.3",
            name: "Texts and Contexts",
            outcome: "Understand how vocabulary and language choices suit different audiences, purposes, and contexts",
            topics: [
              { code: "9ESL2.3.2.1", topic: "Identify English or home language words in formal or informal situations", level: 2 },
              { code: "9ESL2.3.3.1", topic: "Explain choice of vocabulary and language features for particular audience and purpose", level: 3 },
              { code: "9ESL2.3.3.2", topic: "Explain how figurative language is used in oral texts to affect the listener", level: 3 },
              { code: "9ESL2.3.3.3", topic: "Explain how humour and cultural references are used in oral text", level: 3 },
              { code: "9ESL2.3.3.4", topic: "Engage in self-assessment, peer assessment, and cognitive activities", level: 3 }
            ]
          }
        ]
      },
      {
        id: "9ESL3",
        name: "Reading",
        icon: "FileText",
        color: "blue",
        hours: 35,
        weight: 29,
        outcome: "Read and understand a range of text types for social, vocational, and educational purposes",
        subStrands: [
          {
            id: "9ESL3.1",
            name: "Skills and Strategies",
            outcome: "Use reading strategies and metalanguages to analyse a variety of text types",
            topics: [
              { code: "9ESL3.1.2.1", topic: "Identify word etymology for more complex vocabulary in text", level: 2 },
              { code: "9ESL3.1.2.2", topic: "Identify structural features and cohesions used in a text", level: 2 },
              { code: "9ESL3.1.2.3", topic: "Identify more complex metalanguages (symbols, personification, hyperbole, tone, idiomatic language)", level: 2 },
              { code: "9ESL3.1.3.1", topic: "Compare extended texts on similar themes with sequences, cause and effect, fact and opinion", level: 3 },
              { code: "9ESL3.1.3.2", topic: "Compare text features using appropriate metalanguages", level: 3 }
            ]
          },
          {
            id: "9ESL3.2",
            name: "Production",
            outcome: "Read aloud with fluency and respond to texts using evidence and graphic organisers",
            topics: [
              { code: "9ESL3.2.2.1", topic: "Read more complex texts aloud with fluency, expression, and intonation", level: 2 },
              { code: "9ESL3.2.2.2", topic: "Identify key elements in variety of texts (theme, character, setting, plot, tone)", level: 2 },
              { code: "9ESL3.2.3.1", topic: "Describe effects of language choices used to create tone or atmosphere", level: 3 },
              { code: "9ESL3.2.3.2", topic: "Analyse and compare main ideas using graphic organisers", level: 3 },
              { code: "9ESL3.2.3.3", topic: "Respond to texts by selecting appropriate evidence and partial quotes", level: 3 },
              { code: "9ESL3.2.3.4", topic: "Reflect on own reading skills to identify strengths and areas for development", level: 3 }
            ]
          },
          {
            id: "9ESL3.3",
            name: "Texts and Contexts",
            outcome: "Understand how texts are created for particular purposes, audiences, and contexts",
            topics: [
              { code: "9ESL3.3.2.1", topic: "Identify purposes and intended audience of variety of text types", level: 2 },
              { code: "9ESL3.3.2.2", topic: "Identify narrative points of view in variety of text types", level: 2 },
              { code: "9ESL3.3.2.3", topic: "Write main structural features of fictional and persuasive texts", level: 2 },
              { code: "9ESL3.3.3.1", topic: "Explain that different audiences can interpret texts in distinct ways", level: 3 },
              { code: "9ESL3.3.3.2", topic: "Explain how texts are created for particular purposes, audiences, and contexts", level: 3 },
              { code: "9ESL3.3.3.3", topic: "Explain how cultural perspectives are conveyed in texts for particular purposes", level: 3 }
            ]
          }
        ]
      },
      {
        id: "9ESL4",
        name: "Writing",
        icon: "PenTool",
        color: "green",
        hours: 35,
        weight: 24,
        outcome: "Write a range of text types with accuracy, cohesion, and appropriate structure for social, vocational, and educational contexts",
        subStrands: [
          {
            id: "9ESL4.1",
            name: "Skills and Strategies",
            outcome: "Use sentence structures, punctuation, tenses, and imagery accurately in writing",
            topics: [
              { code: "9ESL4.1.2.1", topic: "Identify different types and forms of sentences (simple, compound, complex)", level: 2 },
              { code: "9ESL4.1.2.2", topic: "Use a range of punctuation marks including embedding quotes in sentences", level: 2 },
              { code: "9ESL4.1.2.3", topic: "Rewrite accurate tenses (present continuous, past continuous, future continuous, future perfect continuous)", level: 2 },
              { code: "9ESL4.1.3.1", topic: "Use nominalisation, noun phrases, adverbial phrases, adjective clauses accurately", level: 3 },
              { code: "9ESL4.1.3.2", topic: "Use variety of imagery and figurative language (personification, hyperbole, idiomatic language)", level: 3 },
              { code: "9ESL4.1.3.3", topic: "Apply range of spelling rules and strategies for complex words", level: 3 }
            ]
          },
          {
            id: "9ESL4.2",
            name: "Production",
            outcome: "Plan, draft, edit, and publish cohesive texts for specific purposes and audiences",
            topics: [
              { code: "9ESL4.2.2.1", topic: "Select appropriate topic and identify purpose and audience for a text type", level: 2 },
              { code: "9ESL4.2.2.2", topic: "Use prewriting techniques including planning, drafting, mind maps, and graphic organisers", level: 2 },
              { code: "9ESL4.2.3.1", topic: "Use appropriate language features for the type of text", level: 3 },
              { code: "9ESL4.2.3.2", topic: "Use cohesive devices appropriate to purpose and audience", level: 3 },
              { code: "9ESL4.2.3.3", topic: "Publish and display final copies selecting from a range of software or handwritten form", level: 3 },
              { code: "9ESL4.2.3.4", topic: "Reflect on own writing and identify aspects that are successful and need further development", level: 3 }
            ]
          },
          {
            id: "9ESL4.3",
            name: "Texts and Contexts",
            outcome: "Understand and use different registers and create texts for authentic and imaginary publication contexts",
            topics: [
              { code: "9ESL4.3.2.1", topic: "Identify structural and language features of different registers", level: 2 },
              { code: "9ESL4.3.3.1", topic: "Use and adjust language, form, and register to suit context, purpose, and audience", level: 3 },
              { code: "9ESL4.3.3.2", topic: "Create texts for authentic publication contexts (writing competitions, school website, class anthology)", level: 3 },
              { code: "9ESL4.3.3.3", topic: "Create texts for imaginary publication contexts (graduation speech, interview transcript)", level: 3 }
            ]
          }
        ]
      },
      {
        id: "9ESL5",
        name: "Viewing",
        icon: "Eye",
        color: "teal",
        hours: 35,
        weight: 7,
        outcome: "View, interpret, and create visual and multimodal texts for social, vocational, and educational contexts",
        subStrands: [
          {
            id: "9ESL5.1",
            name: "Skills and Strategies",
            outcome: "Use viewing pathways and identify visual language features in multimodal texts",
            topics: [
              { code: "9ESL5.1.2.1", topic: "Identify viewing pathways suitable to a variety of text types", level: 2 },
              { code: "9ESL5.1.2.2", topic: "Find visual language features used in constructing texts (framing, subject, proxemics, compositions)", level: 2 },
              { code: "9ESL5.1.2.3", topic: "Identify ranges of multimodal texts and their structural features", level: 2 },
              { code: "9ESL5.1.3.1", topic: "Explain how visual or multimodal texts represent similar themes or issues", level: 3 }
            ]
          },
          {
            id: "9ESL5.2",
            name: "Production",
            outcome: "Respond to and create visual and multimodal texts using evidence and analysis",
            topics: [
              { code: "9ESL5.2.2.1", topic: "Identify appropriate evidence and partial quotes in visual texts", level: 2 },
              { code: "9ESL5.2.2.2", topic: "State the tone or atmosphere of a visual text and its effect", level: 2 },
              { code: "9ESL5.2.3.1", topic: "Analyse main ideas in multimodal texts using graphic organisers", level: 3 },
              { code: "9ESL5.2.3.2", topic: "Create visual or multimodal texts for social and academic contexts", level: 3 },
              { code: "9ESL5.2.3.3", topic: "Create structured paragraphs to compare characters, themes, and issues in visual texts", level: 3 }
            ]
          },
          {
            id: "9ESL5.3",
            name: "Texts and Contexts",
            outcome: "Understand how visual texts are created for particular purposes, audiences, and cultural contexts",
            topics: [
              { code: "9ESL5.3.2.1", topic: "Identify purposes and audiences of visual or multimodal texts", level: 2 },
              { code: "9ESL5.3.2.2", topic: "Identify familiar and unfamiliar cultural perspectives in visual or multimodal texts", level: 2 },
              { code: "9ESL5.3.3.1", topic: "Explain how structural features of multimodal texts work to engage audiences", level: 3 },
              { code: "9ESL5.3.3.2", topic: "Explain how cultural perspectives are conveyed in multimodal texts for particular purposes", level: 3 },
              { code: "9ESL5.3.3.3", topic: "Recognise that different audiences can interpret a multimodal or visual text in variety of ways", level: 3 }
            ]
          }
        ]
      }
    ],
    assessmentStructure: {
      internal: { weight: 60, components: ["Internal Examination (Reading 29%, Writing 24%, Viewing 7%)"] },
      external: { weight: 40, components: ["Continuous Assessment (Teacher Design Tasks, Common Assessment Task)"] }
    }
  },
  year10: {
    title: "Year 10 English as Second Language",
    description: "Advanced ESL skills focusing on listening, speaking, reading, writing and viewing in social, vocational, and educational contexts based on the Vanuatu National Syllabus",
    hours: 165,
    strands: [
      {
        id: "10ESL1",
        name: "Listening",
        icon: "Headphones",
        color: "purple",
        hours: 30,
        weight: 10,
        outcome: "Demonstrate understanding of a range of aural texts in English from social, vocational, and educational contexts",
        subStrands: [
          {
            id: "10ESL1.1",
            name: "Skills and Strategies",
            outcome: "Understand diverse aural texts covering various lengths and topics, including cultural references; infer meanings and assess relevance",
            topics: [
              { code: "10ESL1.1.3.1", topic: "Interpret varied sentence structures and complex linguistic features in extended spoken discourse", level: 3 },
              { code: "10ESL1.1.3.2", topic: "Analyse the impact of tone conveyed through intonation, pitch, stress, volume, pacing, and repetition", level: 3 },
              { code: "10ESL1.1.3.4", topic: "Examine how culture affects language, including idioms and slang, in different spoken texts", level: 3 },
              { code: "10ESL1.1.3.5", topic: "Analyse the use of evaluative language in aural texts", level: 3 },
              { code: "10ESL1.1.3.6", topic: "Distinguish between fact and opinion in an aural text", level: 3 },
              { code: "10ESL1.1.3.7", topic: "Analyse diverse aural texts (podcasts, debates, speeches) to infer deeper meanings and thematic connections", level: 3 },
              { code: "10ESL1.1.1.1", topic: "Define key terminology used in vocational and educational aural texts", level: 1 },
              { code: "10ESL1.1.3.9", topic: "Distinguish between presentation styles and rhetorical devices used in aural texts across different contexts", level: 3 }
            ]
          },
          {
            id: "10ESL1.2",
            name: "Production",
            outcome: "Demonstrate understanding of aural texts and use strategies to sustain engagement",
            topics: [
              { code: "10ESL1.2.2.1", topic: "Conduct self/peer assessment of analytical listening skills in discussions", level: 2 },
              { code: "10ESL1.2.2.2", topic: "Apply active listening techniques throughout a discussion", level: 2 },
              { code: "10ESL1.2.3.1", topic: "Analyse aural texts focusing on underlying themes and assumptions", level: 3 },
              { code: "10ESL1.2.3.2", topic: "Summarise discussions and provide constructive feedback through active listening", level: 3 },
              { code: "10ESL1.2.4.1", topic: "Evaluate the effectiveness of speaking strategies used in aural interaction", level: 4 },
              { code: "10ESL1.2.4.3", topic: "Create and present analysis of aural texts using appropriate structure and language", level: 4 }
            ]
          },
          {
            id: "10ESL1.3",
            name: "Texts and Contexts",
            outcome: "Understand verbal and non-verbal cues in aural texts; relevance of language for intended audience and purpose",
            topics: [
              { code: "10ESL1.3.3.1", topic: "Analyse how shifts in tone alter audience understanding in aural texts", level: 3 },
              { code: "10ESL1.3.3.3", topic: "Distinguish between the use of humour and cultural references in aural texts", level: 3 },
              { code: "10ESL1.3.4.1", topic: "Evaluate the impact of humour on audience interpretation, engagement and relatability", level: 4 },
              { code: "10ESL1.3.4.2", topic: "Discuss how verbal cues enhance message and emotional impact in spoken texts", level: 4 },
              { code: "10ESL1.3.3.4", topic: "Analyse how non-verbal cues enhance message and emotional impact in spoken texts", level: 3 },
              { code: "10ESL1.3.4.3", topic: "Discuss how speaker's belief influences purpose and audience engagement", level: 4 },
              { code: "10ESL1.3.4.4", topic: "Evaluate how speaker's cultural background influences interpretation of aural texts", level: 4 }
            ]
          }
        ]
      },
      {
        id: "10ESL2",
        name: "Speaking",
        icon: "MessageSquare",
        color: "orange",
        hours: 30,
        weight: 10,
        outcome: "Use spoken English with clarity, cohesion, and control for a range of social, vocational, and educational contexts",
        subStrands: [
          {
            id: "10ESL2.1",
            name: "Skills and Strategies",
            outcome: "Speak with clarity using idiomatic and formal language, complex grammar, and self-repair strategies",
            topics: [
              { code: "10ESL2.1.2.1", topic: "Use wider range of vocabulary and idiomatic expressions fluently", level: 2 },
              { code: "10ESL2.1.2.2", topic: "Use appropriate language/word choice for formal and informal contexts", level: 2 },
              { code: "10ESL2.1.2.3", topic: "Use correct complex grammatical structures accurately in speeches", level: 2 },
              { code: "10ESL2.1.3.1", topic: "Express views clearly to enhance clarity in multi-part sentences", level: 3 },
              { code: "10ESL2.1.3.2", topic: "Apply advanced self-repair strategies during speech to maintain coherence", level: 3 },
              { code: "10ESL2.1.3.3", topic: "Engage in group discussion ensuring all voices are included and perspectives respectfully considered", level: 3 },
              { code: "10ESL2.1.3.4", topic: "Apply appropriate body language such as gestures to support verbal messages", level: 3 },
              { code: "10ESL2.1.4.1", topic: "Deliver a 3-5 minute presentation with clear structure, engaging openings, and effective conclusions", level: 4 }
            ]
          },
          {
            id: "10ESL2.2",
            name: "Production",
            outcome: "Create oral texts using language features and vocabulary for effective communication",
            topics: [
              { code: "10ESL2.2.2.1", topic: "Use figurative/emotive language to enhance storytelling and capture audience interest", level: 2 },
              { code: "10ESL2.2.3.1", topic: "Apply tone modulation and pacing to convey meaning and maintain audience engagement", level: 3 },
              { code: "10ESL2.2.4.1", topic: "Evaluate peer's presentation focusing on language suitability for audience", level: 4 },
              { code: "10ESL2.2.3.3", topic: "Apply discourse markers to strengthen clarity and flow in oral text", level: 3 },
              { code: "10ESL2.2.4.3", topic: "Produce a persuasive presentation on a chosen topic", level: 4 },
              { code: "10ESL2.2.4.4", topic: "Deliver extended persuasive presentations using rhetorical devices with visual aids", level: 4 }
            ]
          },
          {
            id: "10ESL2.3",
            name: "Texts and Contexts",
            outcome: "Understand effectiveness of spoken English across contexts and explore appropriate use",
            topics: [
              { code: "10ESL2.3.4.1", topic: "Reflect on peer feedback to improve future communications", level: 4 },
              { code: "10ESL2.3.3.2", topic: "Analyse how tone, pace, and register vary in spoken English for formal/informal contexts", level: 3 },
              { code: "10ESL2.3.4.3", topic: "Evaluate effectiveness of language choices across diverse contexts considering audience and purpose", level: 4 },
              { code: "10ESL2.3.3.4", topic: "Analyse how non-verbal communication cues enhance or detract from spoken English", level: 3 },
              { code: "10ESL2.3.4.4", topic: "Evaluate the influence of code-switching in conversations and presentations", level: 4 }
            ]
          }
        ]
      },
      {
        id: "10ESL3",
        name: "Reading",
        icon: "FileText",
        color: "blue",
        hours: 42,
        weight: 35,
        outcome: "Understand texts on familiar and unfamiliar topics; use metalanguage to understand text connections",
        subStrands: [
          {
            id: "10ESL3.1",
            name: "Skills and Strategies",
            outcome: "Use reading strategies to read fluently and with comprehension; deepen understanding and interpret meaning",
            topics: [
              { code: "10ESL3.1.2.1", topic: "Respond to questions at the literal level", level: 2 },
              { code: "10ESL3.1.3.1", topic: "Respond to questions at the inferential level", level: 3 },
              { code: "10ESL3.1.4.1", topic: "Apply comprehension skills to respond at the evaluative level", level: 4 },
              { code: "10ESL3.1.3.2", topic: "Apply advanced reading strategies to understand deeper meanings", level: 3 },
              { code: "10ESL3.1.4.2", topic: "Use critical reading strategies to draw conclusions from various text types", level: 4 },
              { code: "10ESL3.1.3.5", topic: "Explain the impact of mood/tone on meaning using complex metalanguage", level: 3 },
              { code: "10ESL3.1.3.8", topic: "Compare texts with complex themes focusing on narrative techniques and stylistic choices", level: 3 },
              { code: "10ESL3.1.4.3", topic: "Discuss the impact of varied sentence structure and rhetorical devices on meaning", level: 4 }
            ]
          },
          {
            id: "10ESL3.2",
            name: "Production",
            outcome: "Understand language features and structure for meaning; use figurative language and metalanguage",
            topics: [
              { code: "10ESL3.2.2.1", topic: "Read diverse and complex texts with correct phrasing", level: 2 },
              { code: "10ESL3.2.3.1", topic: "Classify readings demonstrating nuances of tone and feeling", level: 3 },
              { code: "10ESL3.2.3.2", topic: "Explain how main ideas and themes are similar across different texts", level: 3 },
              { code: "10ESL3.2.4.1", topic: "Discuss the impact of figurative language techniques on meaning", level: 4 },
              { code: "10ESL3.2.3.3", topic: "Analyse themes, characters, and author's intent across genres", level: 3 },
              { code: "10ESL3.2.4.2", topic: "Discuss the effects of different elements in multiple texts", level: 4 }
            ]
          },
          {
            id: "10ESL3.3",
            name: "Texts and Contexts",
            outcome: "Understand purpose, audience, language and structure of texts from social, vocational and educational contexts",
            topics: [
              { code: "10ESL3.3.4.1", topic: "Discuss how different audiences may interpret the same text", level: 4 },
              { code: "10ESL3.3.4.2", topic: "Judge how texts reflect and influence cultural values within historical contexts", level: 4 },
              { code: "10ESL3.3.3.2", topic: "Analyse the impact of language choices on audience's emotional response", level: 3 },
              { code: "10ESL3.3.4.3", topic: "Evaluate how purpose and audience shape language, format and style", level: 4 },
              { code: "10ESL3.3.3.3", topic: "Explain how structural features of persuasive/fictional texts influence perspectives", level: 3 },
              { code: "10ESL3.3.4.4", topic: "Discuss how visual elements and multimedia enhance overall purpose of texts", level: 4 }
            ]
          }
        ]
      },
      {
        id: "10ESL4",
        name: "Writing",
        icon: "PenTool",
        color: "green",
        hours: 40,
        weight: 35,
        outcome: "Produce well-structured, evidenced texts with strong sense of audience and purpose using complex language structures",
        subStrands: [
          {
            id: "10ESL4.1",
            name: "Skills and Strategies",
            outcome: "Use structure, cohesion, syntax, punctuation; select vocabulary; use quotations accurately in academic writing",
            topics: [
              { code: "10ESL4.1.3.1", topic: "Construct simple, compound, and complex sentence structures with proficiency", level: 3 },
              { code: "10ESL4.1.2.1", topic: "Apply correct punctuation to sentence structure", level: 2 },
              { code: "10ESL4.1.2.3", topic: "Use quotation marks properly in writing to indicate speech and develop tone", level: 2 },
              { code: "10ESL4.1.2.6", topic: "Use suffixes, prefixes, and affixes to expand meaning", level: 2 },
              { code: "10ESL4.1.2.7", topic: "Use homonyms, homophones, and homographs in context", level: 2 },
              { code: "10ESL4.1.3.2", topic: "Rewrite tenses into their subjunctive forms", level: 3 },
              { code: "10ESL4.1.2.11", topic: "Use rhetorical strategies to enhance persuasive or narrative writing", level: 2 },
              { code: "10ESL4.1.3.3", topic: "Use correct cohesive devices/transitional words to connect ideas", level: 3 }
            ]
          },
          {
            id: "10ESL4.2",
            name: "Production",
            outcome: "Produce extended texts using appropriate language, structure, supporting detail; edit for accuracy and clarity",
            topics: [
              { code: "10ESL4.2.3.1", topic: "Conduct self-reflective writing articulating strengths and areas for growth", level: 3 },
              { code: "10ESL4.2.3.4", topic: "Structure writing clearly appropriate to audience and purpose", level: 3 },
              { code: "10ESL4.2.3.5", topic: "Edit writing using comprehensive checklist for clarity, cohesion, sentence construction", level: 3 },
              { code: "10ESL4.2.4.1", topic: "Produce writing with clear appropriate structure", level: 4 },
              { code: "10ESL4.2.4.2", topic: "Produce a written report for oral or written presentation", level: 4 },
              { code: "10ESL4.2.4.3", topic: "Deliver multimedia presentations of final written work ensuring audience engagement", level: 4 }
            ]
          },
          {
            id: "10ESL4.3",
            name: "Texts and Contexts",
            outcome: "Understand register differences between home language and English; demonstrate control over formal and informal register",
            topics: [
              { code: "10ESL4.3.3.1", topic: "Analyse effectiveness of different registers in a range of text types", level: 3 },
              { code: "10ESL4.3.3.2", topic: "Explain the impact of register on audience perception", level: 3 },
              { code: "10ESL4.3.4.1", topic: "Discuss how cultural backgrounds influence register and community style", level: 4 },
              { code: "10ESL4.3.4.5", topic: "Evaluate how register differences between home language and English affect communication", level: 4 },
              { code: "10ESL4.3.4.6", topic: "Produce formal/informal texts that suit specified audiences and purpose", level: 4 }
            ]
          }
        ]
      },
      {
        id: "10ESL5",
        name: "Viewing",
        icon: "Eye",
        color: "teal",
        hours: 23,
        weight: 10,
        outcome: "Understand visual and multimodal texts for school, vocational, and educational contexts",
        subStrands: [
          {
            id: "10ESL5.1",
            name: "Skills and Strategies",
            outcome: "Understand multimodal and visual features used across texts from social, vocational and educational contexts",
            topics: [
              { code: "10ESL5.1.3.1", topic: "Analyse the interplay of visual and textual elements in professional contexts", level: 3 },
              { code: "10ESL5.1.3.2", topic: "Analyse the impact of multimodal elements (sound, graphics, text) on audience engagement", level: 3 },
              { code: "10ESL5.1.4.1", topic: "Evaluate effectiveness of visual styles and design principles in conveying themes", level: 4 },
              { code: "10ESL5.1.4.2", topic: "Evaluate how cultural/social contexts influence interpretation of visual texts", level: 4 }
            ]
          },
          {
            id: "10ESL5.2",
            name: "Production",
            outcome: "Use structural, visual, and multimodal features to create text types; plan, create and edit for cohesion",
            topics: [
              { code: "10ESL5.2.3.1", topic: "Explain how visual language choices shape meaning and affect audience perception", level: 3 },
              { code: "10ESL5.2.4.1", topic: "Evaluate development of characters, themes and issues across visual/multimodal texts", level: 4 },
              { code: "10ESL5.2.3.4", topic: "Analyse how evidence and quotes from texts can be integrated into well-structured sentences", level: 3 },
              { code: "10ESL5.2.4.2", topic: "Create comprehensive multimodal project (documentary, infographic, promotional material)", level: 4 }
            ]
          },
          {
            id: "10ESL5.3",
            name: "Texts and Contexts",
            outcome: "Understand how structural, visual and multimodal features are used for different audiences; how cultural perspectives are communicated",
            topics: [
              { code: "10ESL5.3.3.1", topic: "Explain how techniques and conventions convey meaning and engage audiences across contexts", level: 3 },
              { code: "10ESL5.3.3.2", topic: "Compare persuasive strategies in a TED Talk and a Vlog", level: 3 },
              { code: "10ESL5.3.4.1", topic: "Evaluate narrative structures and visual elements in movies and music videos", level: 4 },
              { code: "10ESL5.3.3.4", topic: "Analyse cultural perspectives in visual or multimodal texts", level: 3 },
              { code: "10ESL5.3.4.3", topic: "Evaluate how integration of various modes contributes to overall message and audience impact", level: 4 }
            ]
          }
        ]
      }
    ],
    assessment: {
      externalExamination: 60,
      internalAssessment: 40,
      strandWeighting: {
        listening: { ee: 0, ia: 10, total: 10 },
        speaking: { ee: 0, ia: 10, total: 10 },
        reading: { ee: 25, ia: 10, total: 35 },
        writing: { ee: 25, ia: 10, total: 35 },
        viewing: { ee: 10, ia: 0, total: 10 }
      }
    }
  },
  year11: {
    title: "Year 11 English as Second Language",
    description: "Foundation ESL skills focusing on literature analysis, reading comprehension, writing, and oral communication",
    hours: 132,
    strands: [
      {
        id: "11ESL1",
        name: "Literature",
        icon: "BookOpen",
        color: "purple",
        hours: 32,
        weight: 24,
        outcome: "Demonstrate an understanding of various forms and elements of style used in visual/oral or written texts and how they help communicate the meaning",
        subStrands: [
          {
            id: "11ESL1.1",
            name: "Visual/Oral and Written Text",
            outcome: "Demonstrate an understanding of various forms and elements of style used in visual/oral or written texts and how they help communicate the meaning",
            topics: [
              { code: "11ESL1.1.1.1", topic: "Identify the various forms of texts (e.g., film, novels, drama, short story, poetry etc.)", level: 1 },
              { code: "11ESL1.1.1.2", topic: "Identify the elements of various types of texts such as sentence structures, choice of words", level: 1 },
              { code: "11ESL1.1.1.3", topic: "Identify author, director, poet, and playwright correctly", level: 1 },
              { code: "11ESL1.1.1.4", topic: "Identify the title of the literary text correctly", level: 1 },
              { code: "11ESL1.1.1.5", topic: "State the theme of the selected text", level: 1 },
              { code: "11ESL1.1.1.6", topic: "Identify techniques used in films or other electronic texts", level: 1 },
              { code: "11ESL1.1.2.1", topic: "Describe the features of the different types of texts", level: 2 },
              { code: "11ESL1.1.2.2", topic: "Describe the tone of a text", level: 2 },
              { code: "11ESL1.1.2.3", topic: "Describe the setting in a selected text", level: 2 },
              { code: "11ESL1.1.2.4", topic: "Describe the character(s) in a selected text", level: 2 },
              { code: "11ESL1.1.2.5", topic: "Describe the character relationships in a selected text", level: 2 },
              { code: "11ESL1.1.2.6", topic: "Describe the conflict in a selected text", level: 2 },
              { code: "11ESL1.1.2.7", topic: "Describe the narrative structure in a selected text", level: 2 },
              { code: "11ESL1.1.3.1", topic: "Explain how the author uses the various forms and elements of style to convey meaning", level: 3 },
              { code: "11ESL1.1.3.2", topic: "Analyse how imagery has been used to achieve its purpose", level: 3 },
              { code: "11ESL1.1.3.3", topic: "Analyse information, ideas, themes, and arguments in print and electronic texts", level: 3 },
              { code: "11ESL1.1.3.4", topic: "Use specific and relevant evidence from a close reading of texts to support interpretations", level: 3 },
              { code: "11ESL1.1.4.1", topic: "Evaluate the effectiveness of the writing elements used by an author to communicate meaning", level: 4 },
              { code: "11ESL1.1.4.2", topic: "Discuss how the features help to reveal ideas/themes/messages with evidence", level: 4 }
            ]
          }
        ]
      },
      {
        id: "11ESL2",
        name: "Reading",
        icon: "FileText",
        color: "blue",
        hours: 44,
        weight: 33,
        outcome: "Demonstrate an understanding of various reading strategies and text types including informative, argumentative, persuasive, and narrative texts",
        subStrands: [
          {
            id: "11ESL2.1",
            name: "Reading Comprehension Skills",
            outcome: "Demonstrate an understanding of the use of different strategies to analyse texts and their communicative purposes",
            topics: [
              { code: "11ESL2.1.1.1", topic: "Use different reading strategies to understand text", level: 1 },
              { code: "11ESL2.1.1.2", topic: "Skim read to identify the main ideas of a passage", level: 1 },
              { code: "11ESL2.1.1.3", topic: "Scan a text for specific information", level: 1 },
              { code: "11ESL2.1.2.1", topic: "List the main and supporting details of a text", level: 2 },
              { code: "11ESL2.1.2.2", topic: "Summarize the main ideas in a text", level: 2 },
              { code: "11ESL2.1.3.1", topic: "Identify signal words that guide a reader through an argument", level: 3 },
              { code: "11ESL2.1.4.1", topic: "Evaluate the merit of what is read to distinguish fact and opinion", level: 4 }
            ]
          },
          {
            id: "11ESL2.2",
            name: "Reading Informative Texts",
            outcome: "Demonstrate an understanding of a range of familiar texts composition and their communicative purposes",
            topics: [
              { code: "11ESL2.2.1.1", topic: "Identify the point of view of an author", level: 1 },
              { code: "11ESL2.2.2.1", topic: "Formulate a concluding statement related to the information presented", level: 2 },
              { code: "11ESL2.2.2.2", topic: "Summarise the information obtained from a text", level: 2 },
              { code: "11ESL2.2.3.1", topic: "Compare and contrast a text to others on similar subjects", level: 3 },
              { code: "11ESL2.2.3.2", topic: "Explain key similarities and differences in purpose, topic, audience, tone", level: 3 },
              { code: "11ESL2.2.4.1", topic: "Comment on language use, structure and layout", level: 4 }
            ]
          },
          {
            id: "11ESL2.3",
            name: "Reading Argumentative Texts",
            outcome: "Demonstrate an understanding of a range of familiar texts with an argumentative function",
            topics: [
              { code: "11ESL2.3.1.1", topic: "Identify the reasoning structure evidenced in key words or phrases", level: 1 },
              { code: "11ESL2.3.2.1", topic: "Distinguish between opinion, anecdote and evidence", level: 2 },
              { code: "11ESL2.3.2.2", topic: "Outline the stages of an argument", level: 2 },
              { code: "11ESL2.3.3.1", topic: "Explain the conclusion and how it relates to the rest of the text", level: 3 },
              { code: "11ESL2.3.4.1", topic: "Evaluate the validity of an argument using examples from the text", level: 4 }
            ]
          },
          {
            id: "11ESL2.4",
            name: "Reading Persuasive Texts",
            outcome: "Demonstrate an understanding of a range of familiar texts which have a persuasive function",
            topics: [
              { code: "11ESL2.4.1.1", topic: "Identify the different types of texts which have a persuasive function", level: 1 },
              { code: "11ESL2.4.1.2", topic: "Indicate the audience to which the text is addressed", level: 1 },
              { code: "11ESL2.4.2.1", topic: "Describe the methods used by an author to communicate ideas", level: 2 },
              { code: "11ESL2.4.2.2", topic: "Describe the techniques used to persuade the audience", level: 2 },
              { code: "11ESL2.4.4.1", topic: "Evaluate the effectiveness of style and persuasive techniques", level: 4 }
            ]
          },
          {
            id: "11ESL2.5",
            name: "Reading Narrative Texts",
            outcome: "Demonstrate an understanding of texts with a predominantly narrative function",
            topics: [
              { code: "11ESL2.5.1.1", topic: "Identify the narrative characteristics of different genres", level: 1 },
              { code: "11ESL2.5.2.1", topic: "Describe the setting, characters, structure of a narrative text", level: 2 },
              { code: "11ESL2.5.2.2", topic: "Describe the narrative structure, mood/atmosphere, tone", level: 2 },
              { code: "11ESL2.5.3.1", topic: "Explain how the structure achieves coherence within its genre", level: 3 },
              { code: "11ESL2.5.3.2", topic: "Explain how language in different genres is chosen and shaped to achieve effects", level: 3 },
              { code: "11ESL2.5.4.1", topic: "Critically analyse ways in which language is used to achieve the author's purpose", level: 4 }
            ]
          }
        ]
      },
      {
        id: "11ESL3",
        name: "Writing",
        icon: "PenTool",
        color: "green",
        hours: 44,
        weight: 33,
        outcome: "Demonstrate proficiency in various forms of writing including critical, persuasive, creative, research, and practical writing",
        subStrands: [
          {
            id: "11ESL3.1",
            name: "Language Tools",
            outcome: "Demonstrate an understanding of grammar, semantics, morphology, and syntax",
            topics: [
              { code: "11ESL3.1.1.1", topic: "Categorize the grammatical class of words (parts of speech)", level: 1 },
              { code: "11ESL3.1.1.2", topic: "Identify the marks of enunciation", level: 1 },
              { code: "11ESL3.1.1.3", topic: "Define the main figures of speech", level: 1 },
              { code: "11ESL3.1.2.1", topic: "Define vocabulary and semantic meaning of words", level: 2 },
              { code: "11ESL3.1.2.2", topic: "Distinguish between signifier and signified", level: 2 },
              { code: "11ESL3.1.2.3", topic: "Differentiate connotation and denotation", level: 2 },
              { code: "11ESL3.1.3.1", topic: "Identify different types of morphemes", level: 3 },
              { code: "11ESL3.1.3.2", topic: "Define suffixes and prefixes", level: 3 },
              { code: "11ESL3.1.3.3", topic: "Identify different types and forms of sentences", level: 3 },
              { code: "11ESL3.1.3.4", topic: "Use punctuation correctly to achieve clarity in writing", level: 3 },
              { code: "11ESL3.1.4.1", topic: "Construct sentences respecting grammar rules", level: 4 },
              { code: "11ESL3.1.4.2", topic: "Explain the functions of words in sentences", level: 4 }
            ]
          },
          {
            id: "11ESL3.2",
            name: "Critical Writing",
            outcome: "Demonstrate an understanding of expository writing with clear structure",
            topics: [
              { code: "11ESL3.2.1.1", topic: "Name the different parts of the essay writing process", level: 1 },
              { code: "11ESL3.2.1.2", topic: "Select an issue of interest", level: 1 },
              { code: "11ESL3.2.2.1", topic: "Describe the different parts of the essay writing process", level: 2 },
              { code: "11ESL3.2.2.2", topic: "List ideas related to selected topic", level: 2 },
              { code: "11ESL3.2.3.1", topic: "Organise ideas using introduction, body, and conclusion", level: 3 },
              { code: "11ESL3.2.3.2", topic: "Use emotive statements, statistics to support arguments", level: 3 },
              { code: "11ESL3.2.4.1", topic: "Produce a final written essay", level: 4 },
              { code: "11ESL3.2.4.2", topic: "Edit drafts of writing toward a final draft", level: 4 }
            ]
          },
          {
            id: "11ESL3.3",
            name: "Persuasive Writing",
            outcome: "Demonstrate an understanding of persuasive writing techniques",
            topics: [
              { code: "11ESL3.3.1.1", topic: "Describe the topic and purpose for writing", level: 1 },
              { code: "11ESL3.3.1.2", topic: "Select/Identify the target audience", level: 1 },
              { code: "11ESL3.3.2.1", topic: "Use the conventions of persuasive writing for different contexts", level: 2 },
              { code: "11ESL3.3.3.1", topic: "Use basic persuasive language and devices to convince audience", level: 3 },
              { code: "11ESL3.3.3.2", topic: "Organise ideas using introduction, body, and conclusion", level: 3 }
            ]
          },
          {
            id: "11ESL3.4",
            name: "Creative Writing",
            outcome: "Demonstrate an understanding of various forms and genres of creative writing",
            topics: [
              { code: "11ESL3.4.1.1", topic: "Select a topic of interest", level: 1 },
              { code: "11ESL3.4.1.2", topic: "Identify the audience, text type, purpose, key words, main ideas", level: 1 },
              { code: "11ESL3.4.2.1", topic: "Use writing conventions accurately", level: 2 },
              { code: "11ESL3.4.3.1", topic: "Structure writing clearly appropriate to audience, purpose and text type", level: 3 },
              { code: "11ESL3.4.3.2", topic: "Develop ideas using themes, setting, characterisation, figurative language", level: 3 },
              { code: "11ESL3.4.4.1", topic: "Produce a piece of writing appropriate for different audiences", level: 4 }
            ]
          },
          {
            id: "11ESL3.5",
            name: "Writing for Research",
            outcome: "Demonstrate an understanding of research writing with accurate referencing",
            topics: [
              { code: "11ESL3.5.1.1", topic: "Identify appropriate methods to collect information", level: 1 },
              { code: "11ESL3.5.2.1", topic: "Gather information from the library and other sources", level: 2 },
              { code: "11ESL3.5.2.2", topic: "Reference the sources used in the research", level: 2 },
              { code: "11ESL3.5.3.1", topic: "Classify, organise and sort information from different sources", level: 3 },
              { code: "11ESL3.5.3.2", topic: "Write a summary of the information collected", level: 3 },
              { code: "11ESL3.5.4.1", topic: "Produce a report using appropriate headings, subheadings, tables, graphs", level: 4 }
            ]
          },
          {
            id: "11ESL3.6",
            name: "Writing for Practical Purposes",
            outcome: "Demonstrate an understanding of practical writing for everyday requirements",
            topics: [
              { code: "11ESL3.6.1.1", topic: "Fill out a variety of forms", level: 1 },
              { code: "11ESL3.6.1.2", topic: "Define a Curriculum Vitae", level: 1 },
              { code: "11ESL3.6.2.1", topic: "Describe the layout and structure of an application letter", level: 2 },
              { code: "11ESL3.6.2.2", topic: "Describe the purpose of a Curriculum Vitae", level: 2 },
              { code: "11ESL3.6.3.1", topic: "Create a schedule of actions/activities (e.g. study plan)", level: 3 },
              { code: "11ESL3.6.4.1", topic: "Write a Curriculum Vitae", level: 4 },
              { code: "11ESL3.6.4.2", topic: "Write an application letter", level: 4 }
            ]
          }
        ]
      },
      {
        id: "11ESL4",
        name: "Listening and Speaking",
        icon: "MessageSquare",
        color: "orange",
        hours: 12,
        weight: 10,
        outcome: "Demonstrate proficiency in listening comprehension and oral communication",
        subStrands: [
          {
            id: "11ESL4.1",
            name: "Listening Comprehension (Aural)",
            outcome: "Demonstrate an understanding of specific information given on familiar and social topics",
            topics: [
              { code: "11ESL4.1.1.1", topic: "Answer questions after listening to an aural text", level: 1 },
              { code: "11ESL4.1.2.1", topic: "List the key ideas from a live presentation", level: 2 },
              { code: "11ESL4.1.2.2", topic: "Ask questions after listening to a speaker", level: 2 },
              { code: "11ESL4.1.3.1", topic: "Design/illustrate/draw images from listening to an aural text", level: 3 },
              { code: "11ESL4.1.4.1", topic: "Summarize ideas from an aural text", level: 4 }
            ]
          },
          {
            id: "11ESL4.2",
            name: "Fluency in Speaking (Oral)",
            outcome: "Demonstrate an understanding of spoken interactions on personal and content area topics",
            topics: [
              { code: "11ESL4.2.1.1", topic: "Use clear descriptions of events or places", level: 1 },
              { code: "11ESL4.2.2.1", topic: "Express views clearly in group discussions", level: 2 },
              { code: "11ESL4.2.2.2", topic: "Give a talk keeping within the allocated time", level: 2 },
              { code: "11ESL4.2.3.1", topic: "Present opinions about an idea with evidence to support argument", level: 3 },
              { code: "11ESL4.2.3.2", topic: "Prepare and deliver a public speech", level: 3 },
              { code: "11ESL4.2.4.1", topic: "Express own opinion about an issue with well-developed ideas", level: 4 }
            ]
          },
          {
            id: "11ESL4.3",
            name: "Accuracy in Speaking",
            outcome: "Demonstrate correct use of grammatical structures in conversational topics",
            topics: [
              { code: "11ESL4.3.1.1", topic: "Use correct grammatical structures appropriately in conversational topics", level: 1 },
              { code: "11ESL4.3.2.1", topic: "Enunciate clearly", level: 2 },
              { code: "11ESL4.3.2.2", topic: "Apply appropriate stress where needed", level: 2 },
              { code: "11ESL4.3.3.1", topic: "Develop appropriate rhythm and intonation patterns", level: 3 },
              { code: "11ESL4.3.3.2", topic: "Paraphrase what is heard", level: 3 },
              { code: "11ESL4.3.4.1", topic: "Restate complex ideas in simple sentences", level: 4 }
            ]
          }
        ]
      }
    ],
    assessment: {
      internalExamination: 60,
      continuousAssessment: 40,
      strandWeighting: {
        literature: { ie: 14, ca: 10, total: 24 },
        reading: { ie: 20, ca: 13, total: 33 },
        writing: { ie: 20, ca: 13, total: 33 },
        listeningAndSpeaking: { ie: 6, ca: 4, total: 10 }
      }
    }
  },
  year12: {
    title: "Year 12 English as Second Language",
    description: "Intermediate ESL skills with deeper literary analysis, complex text types, and advanced writing",
    hours: 165,
    strands: [
      {
        id: "12ESL1",
        name: "Literature",
        icon: "BookOpen",
        color: "purple",
        hours: 40,
        weight: 24,
        outcome: "Demonstrate an understanding of the relationships between various features and events in a selected text and how key elements influence meaning",
        subStrands: [
          {
            id: "12ESL1.1",
            name: "Visual/Oral and Written Text",
            outcome: "Demonstrate an understanding of the relationships between various features and events in a selected text",
            topics: [
              { code: "12ESL1.1.1.1", topic: "Identify the key elements of style of a text", level: 1 },
              { code: "12ESL1.1.1.2", topic: "State the theme, title, setting, key event, type of text, author", level: 1 },
              { code: "12ESL1.1.1.3", topic: "Identify key ideas/messages in a variety of text types", level: 1 },
              { code: "12ESL1.1.2.1", topic: "Describe the features of different types of texts", level: 2 },
              { code: "12ESL1.1.2.2", topic: "Describe an aspect of a text (themes, mood, characterisation)", level: 2 },
              { code: "12ESL1.1.2.3", topic: "Outline the plot of a text", level: 2 },
              { code: "12ESL1.1.3.1", topic: "Explain the purpose of a given text supported by relevant evidence", level: 3 },
              { code: "12ESL1.1.3.2", topic: "Explain how key elements influence meaning (intensive study)", level: 3 },
              { code: "12ESL1.1.3.3", topic: "Explain how authors use rhetorical and literary devices to enhance meaning", level: 3 },
              { code: "12ESL1.1.4.1", topic: "Analyse how language features are used to create voice appropriate to purpose", level: 4 },
              { code: "12ESL1.1.4.2", topic: "Compare and contrast two writers from the same time period", level: 4 },
              { code: "12ESL1.1.4.3", topic: "Critique or state an opinion about the themes, purpose, ideas, style of a text", level: 4 }
            ]
          }
        ]
      },
      {
        id: "12ESL2",
        name: "Reading",
        icon: "FileText",
        color: "blue",
        hours: 56,
        weight: 34,
        outcome: "Demonstrate an understanding of less familiar texts with various communicative purposes",
        subStrands: [
          {
            id: "12ESL2.1",
            name: "Reading Informative Texts",
            outcome: "Demonstrate an understanding of a range of less familiar texts compositions",
            topics: [
              { code: "12ESL2.1.1.1", topic: "Indicate/Identify the genre, theme, purpose, target audience of a text", level: 1 },
              { code: "12ESL2.1.2.1", topic: "Define key/difficult words in a text", level: 2 },
              { code: "12ESL2.1.2.2", topic: "List the main ideas in a text", level: 2 },
              { code: "12ESL2.1.3.1", topic: "Explain the point of view of an author", level: 3 },
              { code: "12ESL2.1.3.2", topic: "Analyse language use, structure and layout", level: 3 },
              { code: "12ESL2.1.4.1", topic: "Evaluate the selection of facts and how language relays key messages", level: 4 }
            ]
          },
          {
            id: "12ESL2.2",
            name: "Reading Argumentative Texts",
            outcome: "Demonstrate an understanding of a range of less familiar texts with argumentative function",
            topics: [
              { code: "12ESL2.2.1.1", topic: "Identify the key message(s) and target audience of an argument", level: 1 },
              { code: "12ESL2.2.2.1", topic: "Outline the stages of an argument", level: 2 },
              { code: "12ESL2.2.2.2", topic: "Describe the reasoning structure evidenced in key words", level: 2 },
              { code: "12ESL2.2.3.1", topic: "Explain how language use, structure and layout help convey the argument", level: 3 },
              { code: "12ESL2.2.4.1", topic: "Evaluate the validity of an argument using examples", level: 4 }
            ]
          },
          {
            id: "12ESL2.3",
            name: "Reading Persuasive Texts",
            outcome: "Demonstrate an understanding of a range of less familiar texts with persuasive function",
            topics: [
              { code: "12ESL2.3.1.1", topic: "Indicate to which audience a text is addressing", level: 1 },
              { code: "12ESL2.3.1.2", topic: "Identify the purpose and key message(s) in a text", level: 1 },
              { code: "12ESL2.3.2.1", topic: "Describe the writing techniques being used to persuade", level: 2 },
              { code: "12ESL2.3.3.1", topic: "Analyse the methods used by the author to communicate ideas", level: 3 },
              { code: "12ESL2.3.4.1", topic: "Discuss the effectiveness of structure to convince readers", level: 4 },
              { code: "12ESL2.3.4.2", topic: "Evaluate the impact of a passage in achieving its desired effect", level: 4 }
            ]
          },
          {
            id: "12ESL2.4",
            name: "Reading Narrative Texts",
            outcome: "Demonstrate an understanding of less familiar texts with narrative function",
            topics: [
              { code: "12ESL2.4.1.1", topic: "Identify the key ideas and narrative characteristics of different genres", level: 1 },
              { code: "12ESL2.4.2.1", topic: "Describe the writing techniques being used to convey key messages", level: 2 },
              { code: "12ESL2.4.3.1", topic: "Analyse the structure of a narrative and how it achieves coherence", level: 3 },
              { code: "12ESL2.4.3.2", topic: "Explain how language in various genres is chosen and shaped", level: 3 },
              { code: "12ESL2.4.4.1", topic: "Justify responses to a text", level: 4 }
            ]
          },
          {
            id: "12ESL2.5",
            name: "Reading Literary Texts (fiction, drama, poetry)",
            outcome: "Demonstrate an understanding of texts with less familiar ideas in a variety of literary genres",
            topics: [
              { code: "12ESL2.5.1.1", topic: "Identify the key messages in a variety of literary texts", level: 1 },
              { code: "12ESL2.5.2.1", topic: "Describe the elements of fiction as well as elements of style", level: 2 },
              { code: "12ESL2.5.2.2", topic: "Describe poetic features of texts", level: 2 },
              { code: "12ESL2.5.3.1", topic: "Compare and contrast two writers from the same time period", level: 3 },
              { code: "12ESL2.5.3.2", topic: "Explain language and sound devices", level: 3 },
              { code: "12ESL2.5.4.1", topic: "Evaluate the effectiveness of structure to communicate meaning", level: 4 }
            ]
          }
        ]
      },
      {
        id: "12ESL3",
        name: "Writing",
        icon: "PenTool",
        color: "green",
        hours: 57,
        weight: 34,
        outcome: "Demonstrate proficiency in advanced writing forms with complex language use",
        subStrands: [
          {
            id: "12ESL3.1",
            name: "Language Tools",
            outcome: "Demonstrate an understanding of the use and effects of different language tools",
            topics: [
              { code: "12ESL3.1.1.1", topic: "Identify the different parts of speech", level: 1 },
              { code: "12ESL3.1.1.2", topic: "Identify characteristics of indirect/direct speech", level: 1 },
              { code: "12ESL3.1.2.1", topic: "Transform direct speech to indirect speech and vice versa", level: 2 },
              { code: "12ESL3.1.2.2", topic: "Explain the values of verb tenses in a text", level: 2 },
              { code: "12ESL3.1.3.1", topic: "Explain the effects of figures of speech in a text", level: 3 },
              { code: "12ESL3.1.3.2", topic: "Use different transitions in a logical argument", level: 3 },
              { code: "12ESL3.1.4.1", topic: "Produce a written piece using a variety of vocabulary and sentence structures", level: 4 }
            ]
          },
          {
            id: "12ESL3.2",
            name: "Critical Writing",
            outcome: "Demonstrate an understanding of writing that clearly communicates critical thinking",
            topics: [
              { code: "12ESL3.2.1.1", topic: "Use prewriting techniques to generate ideas for writing", level: 1 },
              { code: "12ESL3.2.2.1", topic: "Develop/produce a draft of a writing piece", level: 2 },
              { code: "12ESL3.2.2.2", topic: "Give feedback/comments/edits for writing drafts", level: 2 },
              { code: "12ESL3.2.3.1", topic: "Organise and develop ideas in a coherent manner", level: 3 },
              { code: "12ESL3.2.4.1", topic: "Write an 800-word essay demonstrating critical writing", level: 4 }
            ]
          },
          {
            id: "12ESL3.3",
            name: "Persuasive Writing",
            outcome: "Demonstrate an understanding of formal writing to persuade, influence and challenge",
            topics: [
              { code: "12ESL3.3.1.1", topic: "Identify key concepts to develop an argument", level: 1 },
              { code: "12ESL3.3.2.1", topic: "Clearly state an opinion in their piece of writing", level: 2 },
              { code: "12ESL3.3.2.2", topic: "Use conventions of paragraph writing", level: 2 },
              { code: "12ESL3.3.3.1", topic: "Use appropriate emotive words to create mood and tone", level: 3 },
              { code: "12ESL3.3.4.1", topic: "Use examples and evidence to persuade, influence and challenge", level: 4 }
            ]
          },
          {
            id: "12ESL3.4",
            name: "Creative Writing",
            outcome: "Demonstrate an understanding of writing that explores imagination and figurative language",
            topics: [
              { code: "12ESL3.4.1.1", topic: "Select a topic of interest", level: 1 },
              { code: "12ESL3.4.2.1", topic: "Identify the type of writing and purpose", level: 2 },
              { code: "12ESL3.4.3.1", topic: "Structure writing clearly appropriate to audience, purpose and text type", level: 3 },
              { code: "12ESL3.4.4.1", topic: "Produce a creative piece using appropriate writing process", level: 4 }
            ]
          },
          {
            id: "12ESL3.5",
            name: "Writing for Research",
            outcome: "Demonstrate an understanding of a referenced research framework and data synthesis",
            topics: [
              { code: "12ESL3.5.1.1", topic: "Choose a topic and narrow it down to a specific topic", level: 1 },
              { code: "12ESL3.5.2.1", topic: "Identify the appropriate sources of information", level: 2 },
              { code: "12ESL3.5.2.2", topic: "Describe the methodology to be used for data collection", level: 2 },
              { code: "12ESL3.5.3.1", topic: "Formulate focal questions that will expand understanding", level: 3 },
              { code: "12ESL3.5.3.2", topic: "Collect and collate data from a range of sources", level: 3 },
              { code: "12ESL3.5.4.1", topic: "Produce a research report for oral or written presentation", level: 4 }
            ]
          },
          {
            id: "12ESL3.6",
            name: "Writing for Practical Purposes",
            outcome: "Demonstrate an understanding of extended pieces of writing for complex everyday requirements",
            topics: [
              { code: "12ESL3.6.1.1", topic: "Identify the different types of formal letters", level: 1 },
              { code: "12ESL3.6.2.1", topic: "Use simple planning, drafting and editing processes", level: 2 },
              { code: "12ESL3.6.3.1", topic: "Write different types of formal letters using appropriate layout", level: 3 },
              { code: "12ESL3.6.3.2", topic: "Write a memo using appropriate layout", level: 3 },
              { code: "12ESL3.6.4.1", topic: "Use language and tone appropriate to the text's purpose and audience", level: 4 }
            ]
          }
        ]
      },
      {
        id: "12ESL4",
        name: "Listening and Speaking",
        icon: "MessageSquare",
        color: "orange",
        hours: 12,
        weight: 8,
        outcome: "Demonstrate proficiency in listening and speaking in a variety of learning situations",
        subStrands: [
          {
            id: "12ESL4.1",
            name: "Listening Comprehension (Aural)",
            outcome: "Demonstrate an understanding of specific information in more detail with less visual support",
            topics: [
              { code: "12ESL4.1.1.1", topic: "Follow detailed directions or instructions to complete a task", level: 1 },
              { code: "12ESL4.1.2.1", topic: "Identify main ideas of an aural text, news or current affairs", level: 2 },
              { code: "12ESL4.1.3.1", topic: "List ideas from an aural text using note-taking techniques", level: 3 },
              { code: "12ESL4.1.3.2", topic: "Formulate questions for an aural text", level: 3 },
              { code: "12ESL4.1.4.1", topic: "Give a critical analysis of an aural text", level: 4 }
            ]
          },
          {
            id: "12ESL4.2",
            name: "Fluency in Speaking (Oral)",
            outcome: "Demonstrate an understanding of spoken interactions in a variety of learning situations",
            topics: [
              { code: "12ESL4.2.1.1", topic: "Choose a topic based on a literature text for a presentation", level: 1 },
              { code: "12ESL4.2.2.1", topic: "Research/Collect relevant information from a variety of sources", level: 2 },
              { code: "12ESL4.2.3.1", topic: "Present findings in a form of a presentation to the class", level: 3 },
              { code: "12ESL4.2.3.2", topic: "Stage an interview and present findings", level: 3 },
              { code: "12ESL4.2.4.1", topic: "Use features of language of public speaking", level: 4 },
              { code: "12ESL4.2.4.2", topic: "Participate in a debate discussion", level: 4 }
            ]
          },
          {
            id: "12ESL4.3",
            name: "Accuracy in Speaking",
            outcome: "Demonstrate correct use of grammatical structures in conversational topics and discussions",
            topics: [
              { code: "12ESL4.3.1.1", topic: "Identify intonation patterns in tag questions", level: 1 },
              { code: "12ESL4.3.2.1", topic: "Use intonation patterns in tag questions", level: 2 },
              { code: "12ESL4.3.2.2", topic: "Move syllable stress and reduce vowels appropriately", level: 2 },
              { code: "12ESL4.3.3.1", topic: "Change stress to distinguish between noun or verb forms", level: 3 },
              { code: "12ESL4.3.4.1", topic: "Correctly pronounce new words using phonetic knowledge", level: 4 },
              { code: "12ESL4.3.4.2", topic: "Articulate words clearly, verbalising all word endings", level: 4 }
            ]
          }
        ]
      }
    ],
    assessment: {
      externalExamination: 60,
      internalAssessment: 40,
      strandWeighting: {
        literature: { ee: 14, ia: 10, total: 24 },
        reading: { ee: 20, ia: 14, total: 34 },
        writing: { ee: 20, ia: 14, total: 34 },
        listeningAndSpeaking: { ee: 5, ia: 3, total: 8 }
      }
    }
  },
  year13: {
    title: "Year 13 English as Second Language",
    description: "Advanced ESL skills with complex literary analysis, research writing, and sophisticated communication",
    hours: 165,
    strands: [
      {
        id: "13ESL1",
        name: "Literature",
        icon: "BookOpen",
        color: "purple",
        hours: 40,
        weight: 24,
        outcome: "Demonstrate an understanding of various forms and elements of style used in texts and how they enhance effectiveness",
        subStrands: [
          {
            id: "13ESL1.1",
            name: "Visual/Oral and Written Text",
            outcome: "Demonstrate an understanding of various forms and elements of style and how they enhance the effectiveness of the text",
            topics: [
              { code: "13ESL1.1.1.1", topic: "Identify the design elements in various types of texts", level: 1 },
              { code: "13ESL1.1.1.2", topic: "State the theme, title, setting, key event, author of a given text", level: 1 },
              { code: "13ESL1.1.2.1", topic: "Describe an aspect of the text (theme/mood/tone/atmosphere/characterisation)", level: 2 },
              { code: "13ESL1.1.2.2", topic: "Describe an aspect of the language features (word choice/syntax/sound devices/figurative language)", level: 2 },
              { code: "13ESL1.1.3.1", topic: "Analyse how authors and editors use design elements to enhance meaning", level: 3 },
              { code: "13ESL1.1.3.2", topic: "Explain how an aspect contributes to the main message or purpose of text", level: 3 },
              { code: "13ESL1.1.3.3", topic: "Explain how the narrative style and/or point of view conveys messages", level: 3 },
              { code: "13ESL1.1.4.1", topic: "Discuss the effects on the reader of the authors' choices of language and literary devices", level: 4 },
              { code: "13ESL1.1.4.2", topic: "Reflect critically and use logical reasoning in composing a response", level: 4 },
              { code: "13ESL1.1.4.3", topic: "Discuss how the structure of a text works to produce a powerful cohesive message", level: 4 }
            ]
          }
        ]
      },
      {
        id: "13ESL2",
        name: "Reading",
        icon: "FileText",
        color: "blue",
        hours: 56,
        weight: 34,
        outcome: "Demonstrate an understanding of complex, challenging and longer texts",
        subStrands: [
          {
            id: "13ESL2.1",
            name: "Reading Informative Texts",
            outcome: "Demonstrate an understanding of ideas and language features in complex, challenging and longer texts",
            topics: [
              { code: "13ESL2.1.1.1", topic: "Identify the different features of a variety of informative texts", level: 1 },
              { code: "13ESL2.1.1.2", topic: "Identify the point of view of the writer/author", level: 1 },
              { code: "13ESL2.1.2.1", topic: "List the main ideas in the text", level: 2 },
              { code: "13ESL2.1.2.2", topic: "Describe the target audience of a text", level: 2 },
              { code: "13ESL2.1.3.1", topic: "Analyse text, emphasizing purpose, form, structure and language features", level: 3 },
              { code: "13ESL2.1.4.1", topic: "Evaluate the writer's style by commenting on effectiveness of word choice", level: 4 },
              { code: "13ESL2.1.4.2", topic: "Evaluate how language is used to convey the messages", level: 4 }
            ]
          },
          {
            id: "13ESL2.2",
            name: "Reading Argumentative Texts",
            outcome: "Demonstrate an understanding of formal/complex and challenging texts with argumentative function",
            topics: [
              { code: "13ESL2.2.1.1", topic: "Identify the reasoning structure evidenced in key words or phrases", level: 1 },
              { code: "13ESL2.2.1.2", topic: "Identify the conclusion and key ideas of the argument", level: 1 },
              { code: "13ESL2.2.2.1", topic: "Describe the language features in an argumentative text", level: 2 },
              { code: "13ESL2.2.2.2", topic: "Outline the stages of a good argument", level: 2 },
              { code: "13ESL2.2.3.1", topic: "Analyse the structure of an argumentative text", level: 3 },
              { code: "13ESL2.2.4.1", topic: "Evaluate the validity of an argument using examples from the text", level: 4 }
            ]
          },
          {
            id: "13ESL2.3",
            name: "Reading Persuasive Texts",
            outcome: "Demonstrate an understanding of longer and complex texts with persuasive function",
            topics: [
              { code: "13ESL2.3.1.1", topic: "Identify the techniques being used to persuade the audience", level: 1 },
              { code: "13ESL2.3.1.2", topic: "Indicate to which audience it is addressed", level: 1 },
              { code: "13ESL2.3.2.1", topic: "Describe the methods used by an author to communicate ideas", level: 2 },
              { code: "13ESL2.3.2.2", topic: "Summarise the key ideas in a text", level: 2 },
              { code: "13ESL2.3.3.1", topic: "Explain the logical reasoning presented for the argument", level: 3 },
              { code: "13ESL2.3.3.2", topic: "Analyse the methods used by the author to convey ideas", level: 3 },
              { code: "13ESL2.3.4.1", topic: "Evaluate the impact of a passage in achieving its desired effect", level: 4 },
              { code: "13ESL2.3.4.2", topic: "Evaluate the effectiveness of style and persuasive techniques", level: 4 }
            ]
          },
          {
            id: "13ESL2.4",
            name: "Reading Narrative Texts",
            outcome: "Demonstrate an understanding of longer and complex texts with predominantly narrative function",
            topics: [
              { code: "13ESL2.4.1.1", topic: "Identify the narrative characteristics of different genres", level: 1 },
              { code: "13ESL2.4.1.2", topic: "Identify the key ideas in a narrative text", level: 1 },
              { code: "13ESL2.4.2.1", topic: "Describe the narrative characteristics of different genres", level: 2 },
              { code: "13ESL2.4.2.2", topic: "Outline the structure of a narrative", level: 2 },
              { code: "13ESL2.4.3.1", topic: "Explain how a narrative achieves coherence", level: 3 },
              { code: "13ESL2.4.3.2", topic: "Discuss how language in different genres is chosen and shaped", level: 3 },
              { code: "13ESL2.4.4.1", topic: "Justify response to a text by exploring and arguing a point of view", level: 4 },
              { code: "13ESL2.4.4.2", topic: "Critically analyse ways in which language is used to achieve purpose", level: 4 }
            ]
          },
          {
            id: "13ESL2.5",
            name: "Reading Literary Texts (fiction, drama, poetry)",
            outcome: "Demonstrate an understanding of texts with more complex ideas in a variety of literary genres",
            topics: [
              { code: "13ESL2.5.1.1", topic: "Identify the main idea in the text and comment on its development", level: 1 },
              { code: "13ESL2.5.2.1", topic: "Describe how the main idea is developed in the text", level: 2 },
              { code: "13ESL2.5.2.2", topic: "Describe the language features or style of writing used by the author", level: 2 },
              { code: "13ESL2.5.3.1", topic: "Explain the purpose of a text supported by relevant evidence", level: 3 },
              { code: "13ESL2.5.3.2", topic: "Explain the influence of social and historical values on texts", level: 3 },
              { code: "13ESL2.5.4.1", topic: "Discuss how key elements of a novel and poetic forms influence meaning", level: 4 },
              { code: "13ESL2.5.4.2", topic: "Evaluate the implications of beliefs or attitudes to real world context", level: 4 }
            ]
          }
        ]
      },
      {
        id: "13ESL3",
        name: "Writing",
        icon: "PenTool",
        color: "green",
        hours: 57,
        weight: 34,
        outcome: "Demonstrate proficiency in sophisticated writing forms with complex language use",
        subStrands: [
          {
            id: "13ESL3.1",
            name: "Language Tools",
            outcome: "Demonstrate an understanding of different parts of speech and their effects",
            topics: [
              { code: "13ESL3.1.1.1", topic: "Identify the elements of style, parts of speech, cohesive devices, punctuation", level: 1 },
              { code: "13ESL3.1.2.1", topic: "Describe the functions of parts of speech", level: 2 },
              { code: "13ESL3.1.2.2", topic: "Describe the cohesive devices used in a given text", level: 2 },
              { code: "13ESL3.1.3.1", topic: "Explain the explicit or implicit logical relations in a text", level: 3 },
              { code: "13ESL3.1.3.2", topic: "Analyse a figure of speech effectively", level: 3 },
              { code: "13ESL3.1.4.1", topic: "Justify the effects produced by textual elements", level: 4 }
            ]
          },
          {
            id: "13ESL3.2",
            name: "Critical Writing",
            outcome: "Demonstrate an understanding of writing that communicates analytical and critical thinking",
            topics: [
              { code: "13ESL3.2.1.1", topic: "Select an issue", level: 1 },
              { code: "13ESL3.2.2.1", topic: "Organise ideas using appropriate conventions of expository writing", level: 2 },
              { code: "13ESL3.2.3.1", topic: "Use more than one source of information to develop an argument", level: 3 },
              { code: "13ESL3.2.3.2", topic: "Analyse information from different sources in a critical manner", level: 3 },
              { code: "13ESL3.2.4.1", topic: "Develop an integrated perspective and critical response to texts studied", level: 4 },
              { code: "13ESL3.2.4.2", topic: "Produce 500-800 word essay incorporating elements of critical writing", level: 4 }
            ]
          },
          {
            id: "13ESL3.3",
            name: "Persuasive Writing",
            outcome: "Demonstrate an understanding of writing that indicates personal, independent perspectives",
            topics: [
              { code: "13ESL3.3.1.1", topic: "Identify key concepts to develop an argument in an essay", level: 1 },
              { code: "13ESL3.3.2.1", topic: "List points of argument", level: 2 },
              { code: "13ESL3.3.2.2", topic: "Describe a point of argument in detail", level: 2 },
              { code: "13ESL3.3.3.1", topic: "Use evidence to support an argument", level: 3 },
              { code: "13ESL3.3.3.2", topic: "Organise ideas coherently into paragraphs", level: 3 },
              { code: "13ESL3.3.4.1", topic: "Produce clear and coherent writing using conventions of persuasive writing", level: 4 }
            ]
          },
          {
            id: "13ESL3.4",
            name: "Creative Writing",
            outcome: "Demonstrate an understanding of writing that indicates personal imaginative perspectives",
            topics: [
              { code: "13ESL3.4.1.1", topic: "Use writing conventions accurately", level: 1 },
              { code: "13ESL3.4.2.1", topic: "Shows evidence of imaginative thinking", level: 2 },
              { code: "13ESL3.4.3.1", topic: "Use craft control and fluent writing that creates effects appropriate to audience", level: 3 },
              { code: "13ESL3.4.4.1", topic: "Produce an essay by organising material clearly and effectively", level: 4 },
              { code: "13ESL3.4.4.2", topic: "Develop, sustain and/or support ideas convincingly with insight and/or originality", level: 4 }
            ]
          },
          {
            id: "13ESL3.5",
            name: "Writing for Research",
            outcome: "Demonstrate an understanding of a formal referenced research report",
            topics: [
              { code: "13ESL3.5.1.1", topic: "Choose a specific topic/localized topic", level: 1 },
              { code: "13ESL3.5.1.2", topic: "State the purpose of the research and hypothesis", level: 1 },
              { code: "13ESL3.5.2.1", topic: "Formulate focal questions that will expand understanding", level: 2 },
              { code: "13ESL3.5.2.2", topic: "State and outline the methodology to be used", level: 2 },
              { code: "13ESL3.5.3.1", topic: "Collect data from a range of sources using two methods", level: 3 },
              { code: "13ESL3.5.3.2", topic: "Use a variety of references such as in-text referencing", level: 3 },
              { code: "13ESL3.5.3.3", topic: "Analyse and tabulate data collected", level: 3 },
              { code: "13ESL3.5.4.1", topic: "Present the research findings in an appropriate format", level: 4 },
              { code: "13ESL3.5.4.2", topic: "Draw appropriate conclusions and recommendations", level: 4 }
            ]
          },
          {
            id: "13ESL3.6",
            name: "Writing for Practical Purposes",
            outcome: "Demonstrate an understanding of extended pieces of writing for employment purposes",
            topics: [
              { code: "13ESL3.6.1.1", topic: "Write the appropriate address for a formal letter of application", level: 1 },
              { code: "13ESL3.6.2.1", topic: "Sketch an appropriate format/outline for a job application letter", level: 2 },
              { code: "13ESL3.6.2.2", topic: "Use words, sentence forms, structure, and style appropriate to reader and purpose", level: 2 },
              { code: "13ESL3.6.3.1", topic: "Prepare a Curriculum Vitae (CV) or resume", level: 3 },
              { code: "13ESL3.6.3.2", topic: "Self-edit a draft of an application letter", level: 3 },
              { code: "13ESL3.6.4.1", topic: "Write a final job application letter using all appropriate conventions", level: 4 }
            ]
          }
        ]
      },
      {
        id: "13ESL4",
        name: "Listening and Speaking",
        icon: "MessageSquare",
        color: "orange",
        hours: 12,
        weight: 8,
        outcome: "Demonstrate proficiency in complex spoken interactions at an advanced level",
        subStrands: [
          {
            id: "13ESL4.1",
            name: "Listening Comprehension (Aural)",
            outcome: "Demonstrate an understanding of specific information on complex or advanced topics",
            topics: [
              { code: "13ESL4.1.1.1", topic: "State views clearly during class discussions", level: 1 },
              { code: "13ESL4.1.2.1", topic: "Identify the main ideas in a lecture or seminar using outline notes", level: 2 },
              { code: "13ESL4.1.2.2", topic: "Identify supporting details in a lecture or seminar", level: 2 },
              { code: "13ESL4.1.3.1", topic: "Use note-taking methods in recording information", level: 3 },
              { code: "13ESL4.1.3.2", topic: "Provide a summary of group discussions", level: 3 },
              { code: "13ESL4.1.4.1", topic: "Critically analyse the key ideas, techniques and responses from spoken English", level: 4 }
            ]
          },
          {
            id: "13ESL4.2",
            name: "Fluency in Speaking (Oral)",
            outcome: "Demonstrate an understanding of more complex spoken interactions at an advanced level",
            topics: [
              { code: "13ESL4.2.1.1", topic: "Ask questions and comments after a seminar", level: 1 },
              { code: "13ESL4.2.2.1", topic: "Provide accurate detail using the correct verb tenses in all time frames", level: 2 },
              { code: "13ESL4.2.3.1", topic: "Develop/Support ideas by adding comments, explanations, details or examples", level: 3 },
              { code: "13ESL4.2.4.1", topic: "Deliver an oral presentation on a language or literature topic with visual aids", level: 4 }
            ]
          },
          {
            id: "13ESL4.3",
            name: "Accuracy in Speaking",
            outcome: "Demonstrate correct use of grammatical structures to discuss concrete and abstract issues",
            topics: [
              { code: "13ESL4.3.1.1", topic: "Use anecdotes, headlines, facts or statistics to make a captivating introduction", level: 1 },
              { code: "13ESL4.3.2.1", topic: "Provide a purpose for the speech", level: 2 },
              { code: "13ESL4.3.2.2", topic: "Use appropriate pronunciation, stress, rhythm and intonation patterns", level: 2 },
              { code: "13ESL4.3.3.1", topic: "Use features of language of public speaking", level: 3 },
              { code: "13ESL4.3.3.2", topic: "Use appropriate body language such as eye contact and gestures", level: 3 },
              { code: "13ESL4.3.4.1", topic: "Use a variety of communication strategies to bridge gaps in spoken modes", level: 4 },
              { code: "13ESL4.3.4.2", topic: "Deliver an oral presentation on the chosen topic with visual aids", level: 4 }
            ]
          }
        ]
      }
    ],
    assessment: {
      externalExamination: 60,
      internalAssessment: 40,
      strandWeighting: {
        literature: { ee: 14, ia: 10, total: 24 },
        reading: { ee: 20, ia: 14, total: 34 },
        writing: { ee: 20, ia: 14, total: 34 },
        listeningAndSpeaking: { ee: 5, ia: 3, total: 8 }
      }
    }
  }
};

// ESL Yearly Planner - All Year Levels
export const eslYearlyPlanner = {
  year8: {
    term1: {
      weeks: "Weeks 1-10",
      focus: "Listening & Speaking Foundations; Reading Skills Development",
      assessment: "CAT 1 - Listening Comprehension & Oral Presentation",
      units: [
        {
          title: "Listening Skills & Strategies",
          weeks: "Weeks 1-3",
          strand: "Listening",
          subStrand: "8ESL1.1",
          topics: ["Interpret sounds, stress, intonation", "Key words and phrases", "Note-taking skills"]
        },
        {
          title: "Speaking with Confidence",
          weeks: "Weeks 4-6",
          strand: "Speaking",
          subStrand: "8ESL2.1",
          topics: ["Verbal interactions", "Code-switching", "Pronunciation and intonation"]
        },
        {
          title: "Reading Strategies & Comprehension",
          weeks: "Weeks 7-10",
          strand: "Reading",
          subStrand: "8ESL3.1",
          topics: ["Decoding vocabulary", "Skimming and scanning", "Organisational features of texts"]
        }
      ]
    },
    term2: {
      weeks: "Weeks 1-10",
      focus: "Writing Skills & Oral Production",
      assessment: "CAT 2 - Essay Writing & Group Presentation",
      units: [
        {
          title: "Writing Skills & Grammar",
          weeks: "Weeks 1-3",
          strand: "Writing",
          subStrand: "8ESL4.1",
          topics: ["Simple, compound and complex sentences", "Cohesive devices", "Punctuation and spelling"]
        },
        {
          title: "Creating Oral Texts",
          weeks: "Weeks 4-6",
          strand: "Speaking",
          subStrand: "8ESL2.2",
          topics: ["Presenting oral texts", "Collaborative presentations", "Descriptive and emotive language"]
        },
        {
          title: "Reading Production & Response",
          weeks: "Weeks 7-10",
          strand: "Reading",
          subStrand: "8ESL3.2",
          topics: ["Summarising main ideas", "Graphic organisers", "Inferring meaning from texts"]
        }
      ]
    },
    term3: {
      weeks: "Weeks 1-10",
      focus: "Writing Production & Viewing Skills",
      assessment: "CAT 3 - Written Report & Visual Text Creation",
      units: [
        {
          title: "Creating Written Texts",
          weeks: "Weeks 1-4",
          strand: "Writing",
          subStrand: "8ESL4.2",
          topics: ["Planning with graphic organisers", "Different paragraph types", "Letters, reviews, blogs, essays"]
        },
        {
          title: "Viewing Skills & Visual Literacy",
          weeks: "Weeks 5-7",
          strand: "Viewing",
          subStrand: "8ESL5.1",
          topics: ["Interpreting visual texts", "Key features of visual media", "Purpose and audience of visual texts"]
        },
        {
          title: "Creating Visual & Multimodal Texts",
          weeks: "Weeks 8-10",
          strand: "Viewing",
          subStrand: "8ESL5.2",
          topics: ["Creating picture books and posters", "Kastom dance presentations", "Photo essays and advertisements"]
        }
      ]
    },
    term4: {
      weeks: "Weeks 1-10",
      focus: "Texts and Contexts; Review & Examination Preparation",
      assessment: "End of Year Examination",
      units: [
        {
          title: "Understanding Texts & Contexts",
          weeks: "Weeks 1-3",
          strand: "Reading",
          subStrand: "8ESL3.3",
          topics: ["Purpose and audience", "Stereotypes in texts", "Cultural perspectives"]
        },
        {
          title: "Writing for Purpose & Audience",
          weeks: "Weeks 4-6",
          strand: "Writing",
          subStrand: "8ESL4.3",
          topics: ["Generic features of texts", "Language for different genres", "Creating texts for specific audiences"]
        },
        {
          title: "Revision & Exam Preparation",
          weeks: "Weeks 7-10",
          strand: "All Strands",
          subStrand: "All",
          topics: ["Review all strands", "Practice exam tasks", "Self-assessment and reflection"]
        }
      ]
    }
  },
  year9: {
    term1: {
      weeks: "Weeks 1-10",
      focus: "Listening Skills & Speaking Foundations",
      assessment: "CAT 1 - Listening Comprehension & Oral Presentation",
      units: [
        {
          title: "Listening: Skills, Strategies and Production",
          weeks: "Weeks 1-5",
          strand: "Listening",
          subStrand: "9ESL1.1, 9ESL1.2",
          topics: ["Extended aural text analysis", "Idioms and colloquialisms", "Active listening", "Paraphrasing", "Personal response with evidence"]
        },
        {
          title: "Listening: Texts, Contexts and Cultural References",
          weeks: "Weeks 6-7",
          strand: "Listening",
          subStrand: "9ESL1.3",
          topics: ["Verbal and non-verbal cues", "Purpose and audience", "Values and cultural references"]
        },
        {
          title: "Speaking: Interactions, Debates and Public Speaking",
          weeks: "Weeks 8-10",
          strand: "Speaking",
          subStrand: "9ESL2.1, 9ESL2.2",
          topics: ["Classroom interactions", "Public speaking", "Debates and discussions", "Register and formality"]
        }
      ]
    },
    term2: {
      weeks: "Weeks 1-10",
      focus: "Reading Skills & Writing Foundations",
      assessment: "CAT 2 - Reading Analysis & Writing Task",
      units: [
        {
          title: "Reading: Skills, Strategies and Production",
          weeks: "Weeks 1-5",
          strand: "Reading",
          subStrand: "9ESL3.1, 9ESL3.2",
          topics: ["Word etymology", "Metalanguages", "Comparing texts", "Reading aloud", "Graphic organisers"]
        },
        {
          title: "Reading: Texts and Contexts",
          weeks: "Weeks 6-7",
          strand: "Reading",
          subStrand: "9ESL3.3",
          topics: ["Purpose and audience", "Narrative points of view", "Audience interpretation", "Cultural perspectives"]
        },
        {
          title: "Writing: Sentence Structure, Punctuation and Tenses",
          weeks: "Weeks 8-10",
          strand: "Writing",
          subStrand: "9ESL4.1",
          topics: ["Sentence types", "Punctuation marks", "Tense rules", "Active and passive voice"]
        }
      ]
    },
    term3: {
      weeks: "Weeks 1-10",
      focus: "Writing Production & Viewing Skills",
      assessment: "CAT 3 - Writing Portfolio & Visual Text Analysis",
      units: [
        {
          title: "Writing: Production and Publishing",
          weeks: "Weeks 1-3",
          strand: "Writing",
          subStrand: "9ESL4.2",
          topics: ["Nominalisation and imagery", "Planning and drafting", "Publishing and reflection"]
        },
        {
          title: "Writing: Register, Context and Creative Writing",
          weeks: "Weeks 4-5",
          strand: "Writing",
          subStrand: "9ESL4.3",
          topics: ["Registers", "Formal and informal writing", "Authentic and imaginary publication"]
        },
        {
          title: "Viewing: Visual and Multimodal Texts",
          weeks: "Weeks 6-10",
          strand: "Viewing",
          subStrand: "9ESL5.1, 9ESL5.2, 9ESL5.3",
          topics: ["Visual language features", "Three-level questioning", "Multimodal analysis", "Cultural perspectives in visual texts"]
        }
      ]
    },
    term4: {
      weeks: "Weeks 1-10",
      focus: "Assessment, Examination and Reporting",
      assessment: "Internal Examination (60%) + Continuous Assessment (40%)",
      units: [
        {
          title: "Continuous Assessment Tasks",
          weeks: "Weeks 1-2",
          strand: "All Strands",
          subStrand: "All",
          topics: ["Unit tests", "Teacher Design Tasks", "Common Assessment Task"]
        },
        {
          title: "Internal Examination Preparation",
          weeks: "Weeks 3-5",
          strand: "All Strands",
          subStrand: "All",
          topics: ["Reading (29%)", "Writing (24%)", "Viewing (7%)"]
        },
        {
          title: "Examination and Reporting",
          weeks: "Weeks 6-10",
          strand: "All Strands",
          subStrand: "All",
          topics: ["Internal Examination", "Results and reporting", "Reflection and goal setting"]
        }
      ]
    }
  },
  year10: {
    term1: {
      weeks: "Weeks 1-10",
      focus: "Listening, Speaking & Reading Foundations",
      assessment: "Listening and Speaking Tasks + Reading Comprehension",
      units: [
        {
          title: "Listening: Aural Text Analysis",
          weeks: "Weeks 1-3",
          strand: "Listening",
          subStrand: "10ESL1",
          topics: ["Extended spoken discourse analysis", "Tone and intonation", "Culture in language", "Fact vs opinion", "Active listening"]
        },
        {
          title: "Speaking: Presentations and Discussion",
          weeks: "Weeks 4-6",
          strand: "Speaking",
          subStrand: "10ESL2",
          topics: ["Formal/informal register", "Group discussion", "Structured presentations", "Persuasive speaking", "Rhetorical devices"]
        },
        {
          title: "Reading: Comprehension and Critical Analysis",
          weeks: "Weeks 7-10",
          strand: "Reading",
          subStrand: "10ESL3",
          topics: ["Literal and inferential comprehension", "Critical reading strategies", "Mood and tone analysis", "Comparative text analysis", "Figurative language"]
        }
      ]
    },
    term2: {
      weeks: "Weeks 1-10",
      focus: "Writing & Viewing Development",
      assessment: "Extended Writing Task + Multimodal Project",
      units: [
        {
          title: "Reading: Audience and Cultural Context",
          weeks: "Weeks 1-2",
          strand: "Reading",
          subStrand: "10ESL3",
          topics: ["Audience interpretation", "Cultural values in texts", "Language choices and emotion"]
        },
        {
          title: "Writing: Structure, Vocabulary and Register",
          weeks: "Weeks 3-6",
          strand: "Writing",
          subStrand: "10ESL4",
          topics: ["Sentence structures", "Punctuation and quotation", "Vocabulary building", "Subjunctive forms", "Rhetorical strategies", "Cohesive devices", "Register and formal/informal writing"]
        },
        {
          title: "Viewing: Visual and Multimodal Text Analysis",
          weeks: "Weeks 7-10",
          strand: "Viewing",
          subStrand: "10ESL5",
          topics: ["Visual and textual interplay", "Multimodal elements", "Cultural perspectives in media", "Persuasive strategies in TED Talks and Vlogs", "Multimodal project creation"]
        }
      ]
    },
    term3: {
      weeks: "Weeks 1-10",
      focus: "Integration, Advanced Skills & Examination",
      assessment: "Internal and External Examination",
      units: [
        {
          title: "Advanced Listening and Speaking",
          weeks: "Weeks 1-4",
          strand: "Listening / Speaking",
          subStrand: "10ESL1 / 10ESL2",
          topics: ["Diverse aural text analysis", "Speaker purpose and cultural background", "Code-switching", "Extended persuasive presentations"]
        },
        {
          title: "Advanced Reading and Academic Writing",
          weeks: "Weeks 5-6",
          strand: "Reading / Writing",
          subStrand: "10ESL3 / 10ESL4",
          topics: ["Structural features analysis", "Self-reflective writing", "Academic essay writing", "Multimedia presentations"]
        },
        {
          title: "Examination Preparation and Final Assessment",
          weeks: "Weeks 7-10",
          strand: "All",
          subStrand: "All",
          topics: ["Integrated skills practice", "Exam preparation", "Final examination"]
        }
      ]
    }
  },
  year11: {
    term1: {
      weeks: "Weeks 1-10",
      focus: "Literature & Reading Foundations",
      assessment: "CAT 1 - Literature Analysis",
      units: [
        {
          title: "Introduction to Literary Forms",
          weeks: "Weeks 1-3",
          strand: "Literature",
          subStrand: "1.1",
          topics: ["Forms of texts", "Elements of style", "Author identification"]
        },
        {
          title: "Character & Setting Analysis",
          weeks: "Weeks 4-6",
          strand: "Literature",
          subStrand: "1.1",
          topics: ["Character description", "Setting analysis", "Conflict identification"]
        },
        {
          title: "Reading Comprehension Skills",
          weeks: "Weeks 7-10",
          strand: "Reading",
          subStrand: "2.1",
          topics: ["Main ideas", "Supporting details", "Inference skills"]
        }
      ]
    },
    term2: {
      weeks: "Weeks 1-10",
      focus: "Writing & Communication Skills",
      assessment: "TDT 1 - Essay Writing",
      units: [
        {
          title: "Essay Writing Fundamentals",
          weeks: "Weeks 1-4",
          strand: "Writing",
          subStrand: "3.1",
          topics: ["Essay structure", "Topic sentences", "Paragraph development"]
        },
        {
          title: "Narrative & Descriptive Writing",
          weeks: "Weeks 5-7",
          strand: "Writing",
          subStrand: "3.2",
          topics: ["Narrative techniques", "Descriptive language", "Creative writing"]
        },
        {
          title: "Oral Communication",
          weeks: "Weeks 8-10",
          strand: "Listening & Speaking",
          subStrand: "4.1",
          topics: ["Class discussions", "Note-taking", "Oral presentations"]
        }
      ]
    },
    term3: {
      weeks: "Weeks 1-10",
      focus: "Advanced Reading & Literature",
      assessment: "CAT 2 - Reading Comprehension",
      units: [
        {
          title: "Critical Reading Skills",
          weeks: "Weeks 1-4",
          strand: "Reading",
          subStrand: "2.2",
          topics: ["Critical analysis", "Author's purpose", "Text evaluation"]
        },
        {
          title: "Literary Devices & Techniques",
          weeks: "Weeks 5-7",
          strand: "Literature",
          subStrand: "1.1",
          topics: ["Imagery", "Symbolism", "Figurative language"]
        },
        {
          title: "Extended Writing",
          weeks: "Weeks 8-10",
          strand: "Writing",
          subStrand: "3.3",
          topics: ["Extended essays", "Research skills", "Referencing"]
        }
      ]
    },
    term4: {
      weeks: "Weeks 1-10",
      focus: "Revision & Examination Preparation",
      assessment: "Internal Examination",
      units: [
        {
          title: "Literature Review",
          weeks: "Weeks 1-3",
          strand: "Literature",
          subStrand: "1.1",
          topics: ["Text analysis review", "Essay techniques", "Exam strategies"]
        },
        {
          title: "Reading & Writing Integration",
          weeks: "Weeks 4-6",
          strand: "Reading/Writing",
          subStrand: "2.1/3.1",
          topics: ["Comprehension practice", "Essay practice", "Time management"]
        },
        {
          title: "Final Preparation",
          weeks: "Weeks 7-10",
          strand: "All Strands",
          subStrand: "All",
          topics: ["Mock exams", "Revision sessions", "Exam techniques"]
        }
      ]
    }
  },
  year12: {
    term1: {
      weeks: "Weeks 1-10",
      focus: "Advanced Literature Analysis",
      assessment: "CAT 1 - Literary Analysis Essay",
      units: [
        {
          title: "Advanced Text Analysis",
          weeks: "Weeks 1-4",
          strand: "Literature",
          subStrand: "1.1",
          topics: ["Complex themes", "Narrative techniques", "Author's craft"]
        },
        {
          title: "Comparative Literature",
          weeks: "Weeks 5-7",
          strand: "Literature",
          subStrand: "1.1",
          topics: ["Text comparison", "Theme comparison", "Style analysis"]
        },
        {
          title: "Critical Reading",
          weeks: "Weeks 8-10",
          strand: "Reading",
          subStrand: "2.1",
          topics: ["Critical analysis", "Evaluation skills", "Response writing"]
        }
      ]
    },
    term2: {
      weeks: "Weeks 1-10",
      focus: "Research & Academic Writing",
      assessment: "TDT 1 - Research Report",
      units: [
        {
          title: "Research Methodology",
          weeks: "Weeks 1-4",
          strand: "Writing",
          subStrand: "3.2",
          topics: ["Research planning", "Source evaluation", "Note-taking"]
        },
        {
          title: "Academic Writing",
          weeks: "Weeks 5-7",
          strand: "Writing",
          subStrand: "3.2",
          topics: ["Formal writing", "Referencing", "Bibliography"]
        },
        {
          title: "Presentation Skills",
          weeks: "Weeks 8-10",
          strand: "Listening & Speaking",
          subStrand: "4.1",
          topics: ["Oral presentations", "Visual aids", "Public speaking"]
        }
      ]
    },
    term3: {
      weeks: "Weeks 1-10",
      focus: "Creative & Practical Writing",
      assessment: "CAT 2 - Creative Portfolio",
      units: [
        {
          title: "Creative Writing",
          weeks: "Weeks 1-4",
          strand: "Writing",
          subStrand: "3.3",
          topics: ["Short stories", "Poetry", "Drama scripts"]
        },
        {
          title: "Practical Writing",
          weeks: "Weeks 5-7",
          strand: "Writing",
          subStrand: "3.4",
          topics: ["Formal letters", "Reports", "Applications"]
        },
        {
          title: "Advanced Comprehension",
          weeks: "Weeks 8-10",
          strand: "Reading",
          subStrand: "2.2",
          topics: ["Complex texts", "Inference", "Analysis"]
        }
      ]
    },
    term4: {
      weeks: "Weeks 1-10",
      focus: "Examination Preparation",
      assessment: "Internal Examination",
      units: [
        {
          title: "Literature Revision",
          weeks: "Weeks 1-3",
          strand: "Literature",
          subStrand: "1.1",
          topics: ["Text revision", "Essay practice", "Exam techniques"]
        },
        {
          title: "Skills Integration",
          weeks: "Weeks 4-6",
          strand: "All",
          subStrand: "All",
          topics: ["Reading practice", "Writing practice", "Oral practice"]
        },
        {
          title: "Final Examination Prep",
          weeks: "Weeks 7-10",
          strand: "All",
          subStrand: "All",
          topics: ["Mock exams", "Revision", "Exam strategies"]
        }
      ]
    }
  },
  year13: {
    term1: {
      weeks: "Weeks 1-10",
      focus: "Advanced Literary Studies",
      assessment: "CAT 1 - Critical Essay",
      units: [
        {
          title: "Critical Literary Theory",
          weeks: "Weeks 1-4",
          strand: "Literature",
          subStrand: "1.1",
          topics: ["Literary theories", "Critical approaches", "Text deconstruction"]
        },
        {
          title: "World Literature",
          weeks: "Weeks 5-7",
          strand: "Literature",
          subStrand: "1.1",
          topics: ["International texts", "Cultural contexts", "Comparative analysis"]
        },
        {
          title: "Research Writing",
          weeks: "Weeks 8-10",
          strand: "Writing",
          subStrand: "3.5",
          topics: ["Extended research", "Academic conventions", "Critical writing"]
        }
      ]
    },
    term2: {
      weeks: "Weeks 1-10",
      focus: "Research & Professional Skills",
      assessment: "TDT 1 - Major Research Project",
      units: [
        {
          title: "Independent Research",
          weeks: "Weeks 1-5",
          strand: "Writing",
          subStrand: "3.5",
          topics: ["Research project", "Data collection", "Analysis"]
        },
        {
          title: "Professional Writing",
          weeks: "Weeks 6-8",
          strand: "Writing",
          subStrand: "3.6",
          topics: ["CV writing", "Cover letters", "Professional correspondence"]
        },
        {
          title: "Seminar Presentation",
          weeks: "Weeks 9-10",
          strand: "Listening & Speaking",
          subStrand: "4.2",
          topics: ["Academic presentations", "Seminar skills", "Q&A handling"]
        }
      ]
    },
    term3: {
      weeks: "Weeks 1-10",
      focus: "Advanced Communication & Writing",
      assessment: "CAT 2 - Portfolio & Presentation",
      units: [
        {
          title: "Advanced Essay Writing",
          weeks: "Weeks 1-4",
          strand: "Writing",
          subStrand: "3.4",
          topics: ["Argumentative essays", "Analytical essays", "Synthesis"]
        },
        {
          title: "Public Speaking",
          weeks: "Weeks 5-7",
          strand: "Listening & Speaking",
          subStrand: "4.3",
          topics: ["Formal speeches", "Debates", "Impromptu speaking"]
        },
        {
          title: "Exam Preparation",
          weeks: "Weeks 8-10",
          strand: "All",
          subStrand: "All",
          topics: ["Revision", "Practice papers", "Exam strategies"]
        }
      ]
    },
    term4: {
      weeks: "Weeks 1-10",
      focus: "Final Examination Preparation",
      assessment: "Internal & External Examinations",
      units: [
        {
          title: "Comprehensive Review",
          weeks: "Weeks 1-4",
          strand: "All",
          subStrand: "All",
          topics: ["All strands revision", "Past paper practice", "Weak areas focus"]
        },
        {
          title: "Examination Practice",
          weeks: "Weeks 5-7",
          strand: "All",
          subStrand: "All",
          topics: ["Timed essays", "Comprehension practice", "Oral exam prep"]
        },
        {
          title: "Final Preparation",
          weeks: "Weeks 8-10",
          strand: "All",
          subStrand: "All",
          topics: ["Final revision", "Mock exams", "Exam readiness"]
        }
      ]
    }
  }
};

// ESL Teaching Resources
export const eslResources = {
  year8: [
    { title: "Year 8 ESL Foundation Guide", type: "PDF", description: "Comprehensive guide for Year 8 ESL students covering all five strands", unit: "All" },
    { title: "Listening Skills Workbook", type: "PDF", description: "Exercises for developing listening comprehension and note-taking skills", unit: "Listening" },
    { title: "Speaking Practice Activities", type: "PDF", description: "Paired and group speaking activities including role-plays, debates and presentations", unit: "Speaking" },
    { title: "Reading Strategies Guide", type: "PDF", description: "Skills and strategies for reading comprehension, skimming, scanning and inference", unit: "Reading" },
    { title: "Writing Skills Workbook", type: "PDF", description: "Sentence structure, paragraph types, grammar and punctuation exercises", unit: "Writing" },
    { title: "Visual Literacy Guide", type: "PDF", description: "Understanding and creating visual and multimodal texts", unit: "Viewing" },
    { title: "Figurative Language Reference", type: "PDF", description: "Similes, metaphors, idioms and colloquial language guide with Vanuatu examples", unit: "Reading" },
    { title: "Code-Switching Activity Pack", type: "PDF", description: "Activities exploring formal English, Bislama and vernacular language use", unit: "Speaking" }
  ],
  year9: [
    { title: "Year 9 ESL Comprehensive Guide", type: "PDF", description: "Complete guide covering all five strands: Listening, Speaking, Reading, Writing, and Viewing", unit: "All" },
    { title: "Listening Skills Workbook", type: "PDF", description: "Extended aural text analysis, idioms, metalanguages, and active listening exercises", unit: "Listening" },
    { title: "Speaking Practice Activities", type: "PDF", description: "Debates, interviews, public speaking, and figurative language in oral presentations", unit: "Speaking" },
    { title: "Reading Strategies and Analysis Guide", type: "PDF", description: "Word etymology, text comparison, graphic organisers, and critical reading skills", unit: "Reading" },
    { title: "Writing Skills Workbook", type: "PDF", description: "Sentence structures, punctuation, tenses, imagery, and text production for varied audiences", unit: "Writing" },
    { title: "Visual and Multimodal Text Guide", type: "PDF", description: "Viewing pathways, visual language features, three-level questioning, and multimodal text creation", unit: "Viewing" },
    { title: "Idiom and Colloquialism Reference", type: "PDF", description: "Common idioms and colloquialisms with explanations and Vanuatu context examples", unit: "Listening" },
    { title: "Register and Audience Writing Pack", type: "PDF", description: "Formal and informal register activities with authentic and imaginary publication contexts", unit: "Writing" }
  ],
  year10: [
    { title: "Year 10 ESL Guide", type: "PDF", description: "Introduction to ESL for Year 10 students", unit: "All" },
    { title: "Basic Grammar Workbook", type: "PDF", description: "Essential grammar exercises and rules", unit: "Writing" },
    { title: "Reading Skills Worksheets", type: "PDF", description: "Activities for reading comprehension", unit: "Reading" },
    { title: "Vocabulary Lists", type: "PDF", description: "Core vocabulary for Year 10 ESL", unit: "Reading" },
    { title: "Paragraph Writing Guide", type: "PDF", description: "Guide to writing effective paragraphs", unit: "Writing" },
    { title: "Speaking Activities", type: "PDF", description: "Oral communication activities and games", unit: "Speaking" }
  ],
  year11: [
    { title: "Literary Analysis Guide", type: "PDF", description: "Comprehensive guide for analyzing literary texts", unit: "Literature" },
    { title: "Essay Writing Templates", type: "Word", description: "Templates for different essay types", unit: "Writing" },
    { title: "Reading Comprehension Strategies", type: "PDF", description: "Strategies for improving reading skills", unit: "Reading" },
    { title: "Grammar Reference Guide", type: "PDF", description: "Complete grammar reference for ESL students", unit: "Writing" },
    { title: "Vocabulary Building Worksheets", type: "PDF", description: "Worksheets for vocabulary development", unit: "Reading" },
    { title: "Oral Presentation Rubric", type: "PDF", description: "Assessment rubric for oral presentations", unit: "Speaking" }
  ],
  year12: [
    { title: "Advanced Literary Terms", type: "PDF", description: "Dictionary of advanced literary terminology", unit: "Literature" },
    { title: "Research Writing Guide", type: "PDF", description: "Step-by-step guide for research writing", unit: "Writing" },
    { title: "Critical Analysis Framework", type: "PDF", description: "Framework for critical text analysis", unit: "Reading" },
    { title: "Academic Writing Conventions", type: "PDF", description: "Guide to academic writing standards", unit: "Writing" },
    { title: "Presentation Skills Handbook", type: "PDF", description: "Tips and techniques for presentations", unit: "Speaking" },
    { title: "Sample Research Papers", type: "PDF", description: "Exemplar research papers for reference", unit: "Writing" }
  ],
  year13: [
    { title: "Critical Theory Introduction", type: "PDF", description: "Overview of major critical theories", unit: "Literature" },
    { title: "Extended Essay Guidelines", type: "PDF", description: "Guidelines for extended essay writing", unit: "Writing" },
    { title: "Research Methodology Handbook", type: "PDF", description: "Complete guide to research methods", unit: "Writing" },
    { title: "CV & Cover Letter Templates", type: "Word", description: "Professional templates for job applications", unit: "Writing" },
    { title: "Seminar Presentation Guide", type: "PDF", description: "Guide for academic seminar presentations", unit: "Speaking" },
    { title: "Exam Preparation Booklet", type: "PDF", description: "Comprehensive exam preparation materials", unit: "All" }
  ]
};
